import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Helmet } from "react-helmet";
import { Galleria } from "primereact/galleria";
import { useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import Header from "../components/Header";
import PopupModal from "../components/PopUpModal";
import TestimonialsSection from "../components/TestimonialsSection";
import OccasionsNav from "../components/OccasionsNav";
import { MEDIA, mediaUrl } from "../config/media";

/* ─── Brand palette ─────────────────────────────────────────────── */
const C = {
  primary:"#5E0E73", primaryLight:"#6E1A85", primaryDeep:"#3A0847",
  primaryPale:"#F3E5F7", cream:"#E8E1D6", beige:"#D6CFC4",
  gold:"#C9A84C", goldLight:"#E8D5A0", goldBright:"#F5D87A",
  dark:"#1A0030", dark2:"#120020",
};

/* ─── ALL CSS ────────────────────────────────────────────────────── */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;0,900;1,600&family=DM+Sans:wght@300;400;500;600;700&display=swap');
:root{--p:#5E0E73;--pl:#6E1A85;--pd:#3A0847;--pp:#F3E5F7;--cream:#E8E1D6;--beige:#D6CFC4;--gold:#C9A84C;--gl:#E8D5A0;--gb:#F5D87A;--dark:#1A0030;--dark2:#120020;}
*,*::before,*::after{box-sizing:border-box;}
.kp-root,.kp-root *{font-family:'DM Sans',sans-serif;}
.pf{font-family:'Playfair Display',serif!important;}

@keyframes _fadeUp{from{opacity:0;transform:translateY(36px)}to{opacity:1;transform:translateY(0)}}
@keyframes _ticker{from{transform:translateX(0)}to{transform:translateX(-50%)}}
@keyframes _spin{to{transform:rotate(360deg)}}
@keyframes _float{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
@keyframes _pulse{0%,100%{opacity:.7}50%{opacity:1}}
@keyframes _shine{0%,100%{opacity:0;transform:translateX(-100%)}40%,60%{opacity:1}100%{transform:translateX(200%)}}
@keyframes kr-shine{0%,100%{opacity:0;transform:translateX(-100%)}40%,60%{opacity:1}100%{transform:translateX(200%)}}
@keyframes kr-fadeUp{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:translateY(0)}}
@keyframes kvt-pulse{0%,100%{transform:scale(1);opacity:.45}50%{transform:scale(1.2);opacity:.85}}
@keyframes kvt-quoteIn{from{opacity:0;transform:translateX(-14px)}to{opacity:1;transform:translateX(0)}}
@keyframes kvt-nameIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
@keyframes kvt-ctrlIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
@keyframes kvt-eqBar{0%,100%{height:5px}50%{height:18px}}
@keyframes kvt-shine{0%,100%{opacity:0;transform:translateX(-100%)}40%,60%{opacity:1}100%{transform:translateX(200%)}}

.rv{opacity:0;transform:translateY(24px);transition:opacity .6s ease,transform .6s cubic-bezier(.16,1,.3,1);}
.rv.in{opacity:1;transform:translateY(0);}
.rv.d1{transition-delay:.08s}.rv.d2{transition-delay:.16s}.rv.d3{transition-delay:.24s}.rv.d4{transition-delay:.32s}

.kp-sec{padding:72px 0;}.kp-sec-sm{padding:44px 0;}

.cc-tag{display:inline-block;font-size:11px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--gold);margin-bottom:10px;}
.cc-tag-light{color:var(--gl)!important;}
.cc-h{font-family:'Playfair Display',serif;font-size:clamp(26px,4vw,44px);font-weight:700;line-height:1.2;color:var(--dark);}
.cc-h-white{color:#fff!important;}
.cc-h span{color:var(--p);}
.cc-h span.gold{color:var(--gold)!important;-webkit-text-fill-color:unset!important;}
.cc-sub{color:#777;font-size:15px;line-height:1.7;max-width:540px;margin:10px auto 0;}
.cc-sub-light{color:rgba(255,255,255,.55)!important;}

.cc-btn{background:linear-gradient(135deg,var(--p),var(--pl));color:#fff;border:none;border-radius:50px;padding:13px 28px;font-weight:700;font-size:14px;cursor:pointer;transition:transform .25s,opacity .25s;text-decoration:none;display:inline-flex;align-items:center;gap:8px;font-family:'DM Sans',sans-serif;position:relative;}
.cc-btn:hover{transform:translateY(-2px);opacity:.9;color:#fff;}
.cc-btn-outline{background:transparent;color:var(--p);border:2px solid var(--p);border-radius:50px;padding:11px 26px;font-weight:700;font-size:14px;cursor:pointer;transition:transform .25s,background .25s,color .25s;text-decoration:none;display:inline-flex;align-items:center;gap:8px;font-family:'DM Sans',sans-serif;}
.cc-btn-outline:hover{background:var(--p);color:#fff;transform:translateY(-2px);}
.cc-btn-gold{position:relative;overflow:hidden;background:linear-gradient(135deg,#B8860B 0%,var(--gold) 50%,#9A6E10 100%);color:var(--dark);border:none;border-radius:50px;padding:13px 28px;font-weight:800;font-size:14px;cursor:pointer;text-decoration:none;display:inline-flex;align-items:center;gap:8px;font-family:'DM Sans',sans-serif;transition:transform .25s,opacity .25s;box-shadow:0 4px 18px rgba(201,168,76,.35);}
.cc-btn-gold::before{content:'';position:absolute;top:0;left:0;width:100%;height:100%;background:linear-gradient(105deg,transparent 35%,rgba(255,255,255,.35) 50%,transparent 65%);transform:translateX(-100%);animation:_shine 3.5s ease infinite;pointer-events:none;}
.cc-btn-gold:hover{transform:translateY(-2px);opacity:.92;color:var(--dark);}
.wa-btn{background:#25D366;color:#fff;border:none;border-radius:50px;padding:13px 28px;font-weight:700;font-size:14px;cursor:pointer;transition:transform .25s,opacity .25s;text-decoration:none;display:inline-flex;align-items:center;gap:8px;font-family:'DM Sans',sans-serif;}
.wa-btn:hover{transform:translateY(-2px);opacity:.88;color:#fff;}
.cc-nav-btn{width:44px;height:44px;border-radius:50%;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.15);color:#fff;font-size:18px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:transform .2s,background .2s;}
.cc-nav-btn:hover{background:var(--gold);color:var(--dark);border-color:var(--gold);transform:scale(1.08);}

.kp-ticker{background:var(--pd);padding:10px 0;overflow:hidden;border-bottom:1px solid rgba(201,168,76,.15);}
.kp-ticker-track{display:flex;width:max-content;animation:_ticker 26s linear infinite;will-change:transform;}
.kp-ticker-item{color:rgba(255,255,255,.6);font-size:11px;font-weight:600;letter-spacing:1px;text-transform:uppercase;padding:0 40px;border-right:1px solid rgba(255,255,255,.1);white-space:nowrap;}

.kp-hero{position:relative;min-height:84vh;background:radial-gradient(ellipse 75% 55% at 75% 35%,rgba(94,14,115,.45) 0%,transparent 70%),radial-gradient(ellipse 55% 45% at 20% 75%,rgba(201,168,76,.12) 0%,transparent 60%),linear-gradient(135deg,#120020 0%,#1A0030 50%,#0D0017 100%);display:flex;align-items:center;overflow-x:clip;}
.kp-hero-orb{position:absolute;border-radius:50%;filter:blur(70px);pointer-events:none;opacity:.6;}
.kp-hero-cnt{position:relative;z-index:2;padding:clamp(60px,7vw,90px) 0;}
.kp-hero-badge{display:inline-flex;align-items:center;gap:8px;background:rgba(201,168,76,.15);border:1px solid rgba(201,168,76,.4);border-radius:60px;padding:7px 18px;color:var(--gold);font-size:12px;font-weight:700;letter-spacing:1px;text-transform:uppercase;margin-bottom:18px;width:fit-content;backdrop-filter:blur(8px);}
.kp-hero-title{font-family:'Playfair Display',serif;font-size:clamp(32px,4.5vw,54px);font-weight:800;color:#fff;line-height:1.15;margin-bottom:18px;letter-spacing:-.5px;}
.kp-hero-title span{background:linear-gradient(135deg,var(--gl),#fff);-webkit-background-clip:text;-webkit-text-fill-color:transparent;}
.kp-hero-sub{font-size:clamp(14px,1.8vw,16px);color:rgba(255,255,255,.76);line-height:1.7;max-width:520px;margin-bottom:28px;}
.kp-hero-vid-wrap{position:relative;overflow:hidden;background:#000;border:1.5px solid rgba(201,168,76,.4);box-shadow:0 24px 60px rgba(0,0,0,.7),0 0 50px rgba(94,14,115,.3);transition:all .35s ease;width:100%;}
@media (min-width: 768px){
  .kp-hero-vid-wrap{aspect-ratio:16/9;max-width:580px;border-radius:20px;margin:0 auto;}
}
@media (max-width: 767px){
  .kp-hero-vid-wrap{aspect-ratio:9/16;max-width:320px;border-radius:24px;margin:0 auto;}
}
.kp-hero-vid-wrap video{width:100%;height:100%;object-fit:cover;display:block;}
.kp-hero-vid-ovl{position:absolute;inset:0;background:linear-gradient(to top,rgba(18,0,32,.7) 0%,transparent 50%);pointer-events:none;}
.kp-hero-vid-badge{position:absolute;bottom:14px;left:14px;right:14px;display:flex;align-items:center;gap:10px;background:rgba(18,0,32,.8);backdrop-filter:blur(10px);border-radius:12px;padding:9px 14px;border:1px solid rgba(201,168,76,.3);z-index:3;}
.kp-hero-vid-pulse{width:8px;height:8px;border-radius:50%;background:#EF4444;flex-shrink:0;animation:_pulse 1.8s ease-in-out infinite;box-shadow:0 0 8px #EF4444;}
.kp-hero-vid-label{font-size:11.5px;color:rgba(255,255,255,.9);font-weight:600;letter-spacing:.4px;}
.kp-trust-row{display:flex;align-items:center;gap:24px;flex-wrap:wrap;margin-top:32px;padding-top:22px;border-top:1px solid rgba(255,255,255,.12);}
.kp-trust-item .num{font-family:'Playfair Display',serif;font-size:28px;font-weight:800;color:var(--gold);line-height:1;}
.kp-trust-item .lbl{font-size:11px;color:rgba(255,255,255,.6);text-transform:uppercase;letter-spacing:1px;margin-top:4px;font-weight:600;}
.kp-trust-sep{width:1px;height:28px;background:rgba(255,255,255,.15);}

.theme-card{flex-shrink:0;width:280px;border-radius:18px;overflow:hidden;position:relative;cursor:pointer;transition:transform .3s;}
.theme-card:hover{transform:translateY(-6px);}
.theme-img{width:100%;height:320px;object-fit:cover;transition:transform .45s;display:block;}
.theme-card:hover .theme-img{transform:scale(1.05);}
.theme-ovl{position:absolute;inset:0;background:linear-gradient(to top,rgba(26,0,48,.9) 0%,transparent 55%);display:flex;flex-direction:column;justify-content:flex-end;padding:22px;}
.theme-name{font-family:'Playfair Display',serif;color:#fff;font-size:19px;font-weight:700;margin-bottom:4px;}
.theme-booked{color:var(--gl);font-size:12px;font-weight:500;}
.theme-tag{background:rgba(255,255,255,.12);color:rgba(255,255,255,.8);font-size:10px;padding:3px 10px;border-radius:50px;border:1px solid rgba(255,255,255,.15);}
.kp-emoji-badge{position:absolute;top:14px;right:14px;z-index:2;background:rgba(0,0,0,.44);border-radius:50%;width:46px;height:46px;display:flex;align-items:center;justify-content:center;font-size:21px;border:1px solid rgba(255,255,255,.12);}

.svc-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:22px;margin-bottom:38px;}
.svc-card{border-radius:22px;overflow:hidden;background:#fff;border:1px solid rgba(94,14,115,.08);transition:transform .35s;cursor:pointer;position:relative;}
.svc-card:hover{transform:translateY(-10px);}
.svc-card::before{content:'';position:absolute;inset:0;border-radius:22px;box-shadow:0 24px 56px rgba(94,14,115,.18);opacity:0;transition:opacity .35s;z-index:-1;}
.svc-card:hover::before{opacity:1;}
.svc-card::after{content:'';position:absolute;bottom:0;left:50%;width:0;height:2px;background:linear-gradient(90deg,var(--p),var(--gold));border-radius:2px;transition:width .35s,left .35s;}
.svc-card:hover::after{width:100%;left:0;}
.svc-img-wrap{position:relative;height:200px;overflow:hidden;}
.svc-img{width:100%;height:100%;object-fit:cover;display:block;transition:transform .45s;}
.svc-card:hover .svc-img{transform:scale(1.07);}
.svc-glow{position:absolute;inset:0;background:linear-gradient(to top,rgba(94,14,115,.45) 0%,transparent 60%);opacity:0;transition:opacity .3s;}
.svc-card:hover .svc-glow{opacity:1;}
.svc-num{position:absolute;top:12px;right:12px;width:30px;height:30px;border-radius:50%;background:rgba(26,0,48,.6);border:1.5px solid rgba(201,168,76,.5);display:flex;align-items:center;justify-content:center;font-family:'Playfair Display',serif;font-size:12px;font-weight:700;color:var(--gl);}
.svc-pill{position:absolute;bottom:12px;left:12px;background:linear-gradient(135deg,var(--gold),var(--gl));color:var(--dark);font-size:9px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;padding:4px 12px;border-radius:50px;transform:translateY(8px);opacity:0;transition:transform .3s,opacity .3s;}
.svc-card:hover .svc-pill{transform:translateY(0);opacity:1;}
.svc-body{padding:22px 14px 18px;position:relative;}
.svc-float-icon{position:absolute;top:-15px;left:14px;width:32px;height:32px;border-radius:10px;background:linear-gradient(135deg,var(--p),var(--pl));display:flex;align-items:center;justify-content:center;font-size:14px;transition:transform .3s;}
.svc-card:hover .svc-float-icon{transform:rotate(-6deg) scale(1.1);}
.svc-title{font-family:'Playfair Display',serif;font-size:16px;font-weight:700;color:var(--dark);transition:color .2s;}
.svc-card:hover .svc-title{color:var(--p);}
.svc-line{display:block;width:0;height:2px;background:linear-gradient(90deg,var(--p),var(--gold));border-radius:2px;margin:7px 0 9px;transition:width .35s;}
.svc-card:hover .svc-line{width:42px;}
.svc-desc{font-size:12px;color:#888;line-height:1.6;}

.act-card{border-radius:18px;overflow:hidden;background:#fff;border:1px solid rgba(94,14,115,.07);transition:transform .3s;cursor:pointer;position:relative;}
.act-card::before{content:'';position:absolute;inset:0;border-radius:18px;box-shadow:0 16px 40px rgba(94,14,115,.15);opacity:0;transition:opacity .3s;z-index:-1;}
.act-card:hover{transform:translateY(-6px) scale(1.01);}
.act-card:hover::before{opacity:1;}

.gal-bento{display:grid;grid-template-columns:repeat(12,1fr);gap:14px;margin-bottom:8px;}
.gbi-1{grid-column:span 5;grid-row:span 2;min-height:370px;}.gbi-2{grid-column:span 4;min-height:178px;}.gbi-3{grid-column:span 3;min-height:178px;}.gbi-4{grid-column:span 4;min-height:178px;}.gbi-5{grid-column:span 3;min-height:178px;}.gbi-6{grid-column:span 7;min-height:215px;}
.gal-bento-item{position:relative;border-radius:18px;overflow:hidden;cursor:pointer;background:var(--dark);opacity:0;transform:translateY(24px) scale(.98);transition:opacity .55s ease,transform .55s cubic-bezier(.25,1,.5,1);}
.gal-bento-item.in{opacity:1;transform:translateY(0) scale(1);}
.gal-bento-item::before{content:'';position:absolute;inset:0;border-radius:18px;box-shadow:0 20px 56px rgba(94,14,115,.25);opacity:0;transition:opacity .3s;z-index:1;pointer-events:none;}
.gal-bento-item:hover::before{opacity:1;}
.gal-bento-item img{width:100%;height:100%;object-fit:cover;display:block;position:absolute;inset:0;transition:transform .5s;}
.gal-bento-item:hover img{transform:scale(1.05);}
.gal-bento-ovl{position:absolute;inset:0;background:rgba(94,14,115,.32);display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity .3s;font-size:28px;color:#fff;z-index:2;}
.gal-bento-item:hover .gal-bento-ovl{opacity:1;}

.kp-faq-item{background:#fff;border-radius:18px;border:1px solid rgba(94,14,115,.09);overflow:hidden;cursor:pointer;transition:border-color .25s;margin-bottom:12px;}
.kp-faq-item:hover{border-color:rgba(201,168,76,.4);}
.kp-faq-q{padding:18px 22px;display:flex;justify-content:space-between;align-items:center;font-size:15px;font-weight:700;color:var(--dark);gap:14px;}
.kp-faq-a{padding:0 22px 16px;font-size:14px;color:#666;line-height:1.7;}
.kp-faq-arrow{width:28px;height:28px;border-radius:50%;background:rgba(94,14,115,.1);display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:transform .25s,background .25s;font-size:15px;color:var(--p);}
.kp-faq-item.open .kp-faq-arrow{transform:rotate(180deg);background:var(--p);color:#fff;}

.enq-wrap{background:var(--dark);border-radius:28px;overflow:hidden;}
.enq-left{background:linear-gradient(160deg,var(--p) 0%,var(--dark) 100%);padding:44px 32px;display:flex;flex-direction:column;justify-content:space-between;}
.enq-right{background:#fff;padding:40px 36px;}
.enq-label{display:block;font-size:11px;font-weight:700;color:#aaa;text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px;}
.enq-input{width:100%;background:var(--cream);border:1.5px solid rgba(94,14,115,.12);border-radius:12px;padding:13px 16px;font-size:14px;font-family:'DM Sans',sans-serif;outline:none;transition:border-color .2s;color:var(--dark);}
.enq-input:focus{border-color:var(--p);}
.enq-input option{background:var(--dark);color:#fff;}
.enq-contact-info{display:flex;align-items:center;gap:12px;padding:13px 14px;background:rgba(255,255,255,.07);border-radius:14px;margin-bottom:10px;}
.enq-ci-icon{width:38px;height:38px;border-radius:10px;background:rgba(255,255,255,.12);display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0;}
.enq-ci-label{font-size:10px;color:rgba(255,255,255,.45);text-transform:uppercase;letter-spacing:.5px;margin-bottom:2px;}
.enq-ci-val{font-size:13px;color:#fff;font-weight:500;}

.kp-cta-sec{position:relative;overflow:hidden;background:linear-gradient(135deg,var(--pd) 0%,var(--p) 50%,var(--pd) 100%);}
.kp-cta-sec::before{content:'';position:absolute;inset:0;background:radial-gradient(ellipse 60% 80% at 50% 50%,rgba(201,168,76,.07) 0%,transparent 70%);pointer-events:none;}
.kp-divider{display:flex;align-items:center;gap:16px;margin:0 0 44px;}
.kp-dl{flex:1;height:1px;background:linear-gradient(90deg,transparent,rgba(94,14,115,.15),transparent);}
.kp-dg{width:8px;height:8px;background:var(--gold);transform:rotate(45deg);flex-shrink:0;}

/* ── Reels ── */
.kr-sec{padding:100px 0 110px;background:#120020;overflow:hidden;position:relative;font-family:'DM Sans',sans-serif;}
.kr-orb{position:absolute;border-radius:50%;filter:blur(70px);pointer-events:none;}
.kr-container{width:min(1320px,92%);margin:auto;position:relative;z-index:2;}
.kr-header{display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:52px;flex-wrap:wrap;gap:20px;animation:kr-fadeUp .8s cubic-bezier(.16,1,.3,1) both;}
.kr-eyebrow{display:inline-flex;align-items:center;gap:8px;color:#E8D5A0;font-size:11px;font-weight:700;letter-spacing:3px;text-transform:uppercase;margin-bottom:14px;}
.kr-eyebrow-line{width:32px;height:1px;background:#C9A84C;opacity:.6;}
.kr-title{font-family:'Playfair Display',serif;font-size:clamp(32px,5vw,54px);font-weight:900;color:#fff;line-height:1.08;margin:0 0 10px;letter-spacing:-.5px;}
.kr-title em{font-style:italic;color:#C9A84C;}
.kr-sub{color:rgba(255,255,255,.5);font-size:15px;line-height:1.6;max-width:400px;}
.kr-nav{display:flex;gap:12px;align-items:center;}
.kr-nav-btn{width:46px;height:46px;border-radius:50%;background:rgba(255,255,255,.06);border:1.5px solid rgba(255,255,255,.14);color:#fff;font-size:20px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:transform .2s,background .2s;font-weight:700;flex-shrink:0;}
.kr-nav-btn:hover{background:#C9A84C;color:#120020;border-color:#C9A84C;transform:scale(1.08);}
.kr-wrap{overflow:hidden;width:100%;}
.kr-track{display:flex;gap:20px;transition:transform .65s cubic-bezier(.25,1,.5,1);will-change:transform;}
.kr-card{position:relative;width:260px;min-width:260px;height:470px;border-radius:26px;overflow:hidden;background:#000;border:1px solid rgba(255,255,255,.07);transition:transform .4s cubic-bezier(.25,1,.5,1);flex-shrink:0;cursor:pointer;}
.kr-card:hover{transform:translateY(-10px) scale(1.02);border-color:rgba(201,168,76,.5);}
.kr-video{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;}
.kr-ovl{position:absolute;inset:0;background:linear-gradient(to top,rgba(13,0,24,.95) 0%,rgba(13,0,24,.4) 45%,rgba(13,0,24,.08) 100%);pointer-events:none;}
.kr-views{position:absolute;top:14px;left:14px;z-index:5;padding:5px 11px;border-radius:999px;background:rgba(0,0,0,.5);border:1px solid rgba(255,255,255,.12);color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;gap:5px;}
.kr-play{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) scale(.85);z-index:5;width:52px;height:52px;border-radius:50%;background:rgba(201,168,76,.9);display:flex;align-items:center;justify-content:center;font-size:18px;opacity:0;transition:opacity .3s,transform .3s;}
.kr-card:hover .kr-play{opacity:1;transform:translate(-50%,-50%) scale(1);}
.kr-info{position:absolute;left:0;right:0;bottom:0;z-index:5;padding:20px 18px 22px;}
.kr-tag-pill{display:inline-block;background:rgba(201,168,76,.2);border:1px solid rgba(201,168,76,.45);color:#C9A84C;font-size:9px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;padding:3px 10px;border-radius:50px;margin-bottom:8px;}
.kr-info h3{margin:0;color:#fff;font-family:'Playfair Display',serif;font-size:17px;font-weight:700;line-height:1.3;}
.kr-dots{display:flex;gap:8px;justify-content:center;margin-top:36px;}
.kr-dot{height:8px;border-radius:4px;background:rgba(255,255,255,.2);border:none;cursor:pointer;transition:all .4s;}
.kr-dot.active{width:28px!important;background:linear-gradient(90deg,#C9A84C,#E8D5A0);}
.kr-cta{margin-top:52px;display:flex;align-items:center;justify-content:center;gap:16px;flex-wrap:wrap;}
.kr-btn-gold{position:relative;overflow:hidden;background:linear-gradient(135deg,#B8860B,#C9A84C,#F5D87A,#C9A84C,#9A6E10);color:#120020;border:none;border-radius:50px;padding:13px 30px;font-weight:800;font-size:14px;cursor:pointer;display:inline-flex;align-items:center;gap:8px;font-family:'DM Sans',sans-serif;text-decoration:none;transition:transform .25s,opacity .25s;}
.kr-btn-gold::before{content:'';position:absolute;top:0;left:0;width:100%;height:100%;background:linear-gradient(105deg,transparent 35%,rgba(255,255,255,.35) 50%,transparent 65%);transform:translateX(-100%);animation:kr-shine 3.5s ease infinite;pointer-events:none;}
.kr-btn-gold:hover{transform:translateY(-2px);opacity:.92;color:#120020;}
.kr-btn-ig{display:inline-flex;align-items:center;gap:9px;border:1.5px solid rgba(255,255,255,.2);background:rgba(255,255,255,.05);color:#fff;border-radius:50px;padding:12px 28px;font-weight:700;font-size:14px;cursor:pointer;font-family:'DM Sans',sans-serif;text-decoration:none;transition:transform .25s,opacity .25s;}
.kr-btn-ig:hover{border-color:rgba(201,168,76,.5);color:#C9A84C;transform:translateY(-2px);}

/* ── Video Testimonials ── */

@keyframes vts-pulse {
  0% { transform: translate(-50%, -50%) scale(1); opacity: .55; }
  100% { transform: translate(-50%, -50%) scale(1.75); opacity: 0; }
}
.cc-play-button-wrapper {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 72px;
  height: 72px;
  z-index: 5;
  pointer-events: all;
  cursor: pointer;
}
.cc-play-pulse {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 1.5px solid rgba(201, 168, 76, 0.65);
  transform: translate(-50%, -50%);
  pointer-events: none;
  animation: vts-pulse 2.5s ease-out infinite;
}
.cc-play-btn {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: rgba(10, 0, 20, 0.55);
  border: 1.5px solid rgba(201, 168, 76, 0.5);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all .3s cubic-bezier(.25,1,.5,1);
  box-shadow: 0 10px 40px rgba(0,0,0,.6);
  cursor: pointer;
}
.cc-play-btn:hover {
  transform: scale(1.08);
  border-color: #C9A84C;
  background: rgba(94, 14, 115, 0.65);
  box-shadow: 0 14px 45px rgba(201, 168, 76, 0.4);
}
.cc-play-btn svg { fill: #fff; transition: fill .2s; }
.cc-play-btn:hover svg { fill: #fff; }

.kvt-sec{background:#120020;font-family:'DM Sans',sans-serif;position:relative;overflow:hidden;}
.kvt-sec::before{content:'';position:absolute;inset:0;background:radial-gradient(ellipse 65% 50% at 20% 10%,rgba(94,14,115,.28) 0%,transparent 65%),radial-gradient(ellipse 50% 40% at 80% 90%,rgba(201,168,76,.08) 0%,transparent 65%);pointer-events:none;z-index:0;}
.kvt-sec::after{content:'';position:absolute;top:0;left:50%;transform:translateX(-50%);width:180px;height:2px;background:linear-gradient(90deg,transparent,#C9A84C,transparent);}
.kvt-header{padding:72px clamp(20px,5vw,60px) 52px;display:flex;align-items:flex-end;justify-content:space-between;flex-wrap:wrap;gap:20px;position:relative;z-index:2;}
.kvt-header::after{content:'';position:absolute;bottom:0;left:clamp(20px,5vw,60px);right:clamp(20px,5vw,60px);height:1px;background:linear-gradient(90deg,transparent,rgba(201,168,76,.25),transparent);}
.kvt-eyebrow{display:flex;align-items:center;gap:10px;font-size:11px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:#C9A84C;margin-bottom:14px;}
.kvt-eyebrow-line{width:26px;height:1px;background:#C9A84C;opacity:.55;}
.kvt-h{font-family:'Playfair Display',serif;font-size:clamp(28px,4vw,46px);font-weight:700;color:#fff;line-height:1.1;margin:0;}
.kvt-h em{font-style:italic;color:#C9A84C;}
.kvt-counter{font-family:'Playfair Display',serif;font-size:14px;color:rgba(255,255,255,.3);flex-shrink:0;align-self:flex-end;}
.kvt-counter span{color:#fff;font-size:18px;font-weight:700;}
.kvt-stage{display:grid;grid-template-columns:1fr 340px;min-height:580px;position:relative;z-index:1;}
.kvt-pane{position:relative;display:flex;align-items:center;justify-content:center;padding:40px 0 40px clamp(20px,5vw,60px);overflow:hidden;}
.kvt-glow{position:absolute;width:420px;height:420px;border-radius:50%;background:radial-gradient(circle,rgba(94,14,115,.22) 0%,transparent 70%);top:50%;left:45%;transform:translate(-50%,-50%);pointer-events:none;z-index:0;transition:background .5s;}
.kvt-glow.active{background:radial-gradient(circle,rgba(201,168,76,.1) 0%,rgba(94,14,115,.15) 50%,transparent 70%);}
.kvt-stage{display:grid;grid-template-columns:1fr 340px;min-height:560px;position:relative;z-index:1;}
.kvt-pane{position:relative;display:flex;align-items:center;justify-content:center;padding:40px clamp(20px,4vw,50px);overflow:hidden;}

/* ─── reel container — 16:9 LANDSCAPE ON PC VIEW ─── */
.kvt-reel{
  position:relative;
  width:100%;
  max-width:850px;
  aspect-ratio:16/9;
  border-radius:18px;
  overflow:hidden;
  background:#08020F;
  border:1px solid rgba(201,168,76,.35);
  box-shadow:0 30px 80px rgba(0,0,0,.75), 0 0 50px rgba(94,14,115,.25);
  cursor:pointer;
  transition:border-color .35s, box-shadow .4s, transform .3s;
  z-index:1;
  flex-shrink:0;
  -webkit-tap-highlight-color:transparent;
  user-select:none;
}
.kvt-reel:hover,.kvt-reel.playing{
  border-color:rgba(201,168,76,.65);
  box-shadow:0 40px 100px rgba(0,0,0,.85), 0 0 60px rgba(201,168,76,.2);
}
.kvt-video{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;display:block;background:#08020F;}
.kvt-notch{display:none;}

/* side arrow buttons */
.kvt-side-arr{
  position:absolute;
  top:50%;
  transform:translateY(-50%);
  width:44px;
  height:44px;
  border-radius:50%;
  background:rgba(255,255,255,.08);
  border:1px solid rgba(255,255,255,.2);
  color:rgba(255,255,255,.85);
  font-size:24px;
  font-weight:700;
  display:flex;
  align-items:center;
  justify-content:center;
  cursor:pointer;
  z-index:10;
  transition:all .25s cubic-bezier(.25,1,.5,1);
  backdrop-filter:blur(10px);
  -webkit-tap-highlight-color:transparent;
  user-select:none;
  line-height:1;
}
.kvt-side-arr:hover{
  background:#C9A84C;
  color:#1A0030;
  border-color:#C9A84C;
  box-shadow:0 6px 24px rgba(201,168,76,.5);
  transform:translateY(-50%) scale(1.1);
}
.kvt-side-arr:active{transform:translateY(-50%) scale(0.94);}
.kvt-side-arr.prev{left:16px;}
.kvt-side-arr.next{right:16px;}

@media (max-width: 1024px){
  .kvt-stage{grid-template-columns:1fr 280px;}
}

@media (max-width: 768px){
  .kvt-stage{grid-template-columns:1fr;grid-template-rows:auto auto;}
  .kvt-pane{padding:30px 16px;min-height:auto;}
  .kvt-side-arr.prev{left:6px;}
  .kvt-side-arr.next{right:6px;}
  .kvt-side-arr{width:36px;height:36px;font-size:18px;background:rgba(201,168,76,.2);border-color:rgba(201,168,76,.4);color:#E8D5A0;}
  .kvt-queue{border-left:none;border-top:1px solid rgba(255,255,255,.08);max-height:none;}
  .kvt-queue-list{display:flex;flex-direction:row;overflow-x:auto;overflow-y:hidden;padding:10px 16px 14px;gap:10px;-webkit-overflow-scrolling:touch;}
  .kvt-queue-list::-webkit-scrollbar{display:none;}
  .kvt-q-item{flex-direction:column;align-items:center;text-align:center;min-width:84px;border-left:none;border-bottom:3px solid transparent;padding:8px;border-radius:12px;}
  .kvt-q-item.active{border-bottom-color:#C9A84C;border-left-color:transparent;}
  .kvt-q-thumb{width:50px;height:74px;}
  .kvt-q-num{display:none;}
}

/* ─── Mobile View: PORTRAIT (9 / 14 phone style) ─── */
@media (max-width: 767px){
  .kvt-reel{
    width:min(82vw,320px) !important;
    max-width:320px !important;
    aspect-ratio:9/14 !important;
    border-radius:24px !important;
    border:1px solid rgba(201,168,76,.35) !important;
  }
  .kvt-video{object-fit:cover !important;}
  .kvt-notch{display:block !important;position:absolute;top:12px;left:50%;transform:translateX(-50%);width:70px;height:4px;background:rgba(255,255,255,.15);border-radius:2px;z-index:8;}
}
.kvt-reel:hover,.kvt-reel.playing{border-color:rgba(201,168,76,.5);}
.kvt-notch{position:absolute;top:14px;left:50%;transform:translateX(-50%);width:80px;height:5px;background:rgba(255,255,255,.1);border-radius:3px;z-index:8;pointer-events:none;}
.kvt-prog{position:absolute;top:0;left:0;right:0;height:3px;background:rgba(255,255,255,.08);z-index:6;}
.kvt-prog-fill{height:100%;background:linear-gradient(90deg,#5E0E73,#C9A84C);border-radius:0 2px 2px 0;transition:width .1s linear;}
.kvt-video{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;}
.kvt-ovl{position:absolute;inset:0;background:linear-gradient(to top,rgba(13,0,24,.96) 0%,rgba(13,0,24,.22) 45%,transparent 100%),linear-gradient(to bottom,rgba(13,0,24,.5) 0%,transparent 25%);pointer-events:none;z-index:2;}
.kvt-pulse-ring{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:68px;height:68px;border-radius:50%;border:1.5px solid rgba(201,168,76,.5);z-index:4;pointer-events:none;animation:kvt-pulse 2.4s ease-in-out infinite;}
.kvt-reel.playing .kvt-pulse-ring{display:none;}
.kvt-play-area{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;z-index:5;pointer-events:none;}
.kvt-play-btn{width:68px;height:68px;border-radius:50%;background:rgba(255,255,255,.12);border:2px solid rgba(255,255,255,.35);display:flex;align-items:center;justify-content:center;transition:all .3s cubic-bezier(.25,1,.5,1);opacity:0;transform:scale(.8);pointer-events:all;cursor:pointer;}
.kvt-play-btn.show{opacity:1;transform:scale(1);animation:kvt-ctrlIn .22s ease-out;}
.kvt-play-btn:hover{background:#C9A84C;border-color:#C9A84C;transform:scale(1.08);}
.kvt-play-btn:hover svg{fill:#1A0030;}
.kvt-play-btn svg{fill:#fff;transition:fill .2s;}
@media(hover:none) and (pointer:coarse){
  .kvt-play-btn{opacity:1!important;transform:scale(1)!important;background:rgba(201,168,76,.85);}
  .kvt-play-btn svg{fill:#1A0030;}
  .kvt-hint{display:none!important;}
}
.kvt-top-ctrl{position:absolute;top:24px;right:14px;z-index:7;display:flex;flex-direction:column;align-items:center;gap:8px;opacity:0;pointer-events:none;transition:opacity .22s;}
.kvt-top-ctrl.show{opacity:1;pointer-events:all;}
.kvt-ctrl-btn{width:34px;height:34px;border-radius:50%;background:rgba(0,0,0,.55);border:1px solid rgba(255,255,255,.15);color:#fff;font-size:13px;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:transform .2s,background .2s;}
.kvt-ctrl-btn:hover{background:rgba(201,168,76,.35);border-color:#C9A84C;}
.kvt-hint{font-size:10px;color:rgba(255,255,255,.4);letter-spacing:.5px;text-transform:uppercase;background:rgba(0,0,0,.45);padding:3px 8px;border-radius:6px;border:1px solid rgba(255,255,255,.08);white-space:nowrap;}
.kvt-info{position:absolute;bottom:0;left:0;right:0;padding:18px 16px 22px;z-index:4;}
.kvt-stars{display:flex;gap:3px;margin-bottom:9px;}
.kvt-quote{font-family:'Playfair Display',serif;font-size:13px;font-style:italic;font-weight:600;color:#fff;line-height:1.55;margin:0 0 14px;animation:kvt-quoteIn .4s cubic-bezier(.25,1,.5,1) both;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;}
.kvt-quote::before{content:'"';font-size:1.5em;line-height:0;vertical-align:-.35em;color:#C9A84C;margin-right:3px;opacity:.8;}
.kvt-identity{display:flex;align-items:center;gap:10px;animation:kvt-nameIn .4s cubic-bezier(.25,1,.5,1) .07s both;}
.kvt-avatar{width:38px;height:38px;border-radius:50%;border:2px solid rgba(201,168,76,.55);background:linear-gradient(135deg,#5E0E73,#6E1A85);display:flex;align-items:center;justify-content:center;font-family:'Playfair Display',serif;font-size:12px;font-weight:700;color:#E8D5A0;flex-shrink:0;}
.kvt-name{font-family:'Playfair Display',serif;font-size:13px;font-weight:700;color:#fff;margin-bottom:3px;}
.kvt-meta{display:flex;align-items:center;gap:5px;flex-wrap:wrap;}
.kvt-pill{background:rgba(201,168,76,.2);border:1px solid rgba(201,168,76,.4);color:#E8D5A0;font-size:8px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;padding:2px 8px;border-radius:50px;}
.kvt-loc{font-size:10px;color:rgba(255,255,255,.4);}
.kvt-transition{position:absolute;inset:0;background:#120020;z-index:10;opacity:0;pointer-events:none;transition:opacity .28s;}
.kvt-transition.show{opacity:1;pointer-events:all;}
.kvt-queue{background:rgba(255,255,255,.025);border-left:1px solid rgba(255,255,255,.06);display:flex;flex-direction:column;overflow:hidden;}
.kvt-queue-header{padding:24px 24px 18px;border-bottom:1px solid rgba(255,255,255,.06);flex-shrink:0;}
.kvt-queue-title{font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:rgba(255,255,255,.3);}
.kvt-queue-list{flex:1;overflow-y:auto;padding:12px 0;scrollbar-width:thin;scrollbar-color:rgba(201,168,76,.2) transparent;}
.kvt-queue-list::-webkit-scrollbar{width:4px;}.kvt-queue-list::-webkit-scrollbar-thumb{background:rgba(201,168,76,.2);border-radius:2px;}
.kvt-q-item{display:flex;align-items:center;gap:14px;padding:12px 20px;cursor:pointer;transition:background .2s;border-left:3px solid transparent;}
.kvt-q-item:hover{background:rgba(255,255,255,.04);}
.kvt-q-item.active{background:rgba(201,168,76,.07);border-left-color:#C9A84C;}
.kvt-q-thumb{width:50px;height:74px;border-radius:10px;overflow:hidden;flex-shrink:0;position:relative;border:1px solid rgba(255,255,255,.07);display:flex;align-items:center;justify-content:center;transition:border-color .2s;}
.kvt-q-item.active .kvt-q-thumb{border-color:rgba(201,168,76,.4);}
.kvt-q-bg{position:absolute;inset:0;background:linear-gradient(155deg,#3A0847 0%,#5E0E73 60%,rgba(201,168,76,.25) 100%);}
.kvt-q-init{position:relative;z-index:1;font-family:'Playfair Display',serif;font-size:13px;font-weight:700;color:rgba(255,255,255,.75);}
.kvt-q-item.active .kvt-q-init{color:#E8D5A0;}
.kvt-q-eq-wrap{position:absolute;inset:0;background:rgba(13,0,24,.65);display:flex;align-items:center;justify-content:center;border-radius:9px;z-index:2;}
.kvt-q-eq{display:flex;align-items:flex-end;gap:2.5px;height:18px;}
.kvt-q-bar{width:3px;background:#C9A84C;border-radius:2px;animation:kvt-eqBar 1s ease-in-out infinite;}
.kvt-q-bar:nth-child(2){animation-delay:.18s;}.kvt-q-bar:nth-child(3){animation-delay:.36s;}
.kvt-q-info{flex:1;min-width:0;}
.kvt-q-name{font-family:'Playfair Display',serif;font-size:13px;font-weight:700;color:#fff;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-bottom:3px;}
.kvt-q-item.active .kvt-q-name{color:#E8D5A0;}
.kvt-q-event{font-size:11px;color:rgba(255,255,255,.35);}
.kvt-q-num{font-size:11px;color:rgba(255,255,255,.2);flex-shrink:0;font-family:'Playfair Display',serif;}
.kvt-q-item.active .kvt-q-num{color:#C9A84C;font-weight:700;}
.kvt-queue-nav{padding:16px 20px;border-top:1px solid rgba(255,255,255,.06);display:flex;align-items:center;justify-content:space-between;flex-shrink:0;}
.kvt-nav-label{font-size:11px;color:rgba(255,255,255,.25);letter-spacing:1px;text-transform:uppercase;}
.kvt-nav-arrows{display:flex;gap:8px;}
.kvt-nav-btn{width:38px;height:38px;border-radius:50%;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.1);color:rgba(255,255,255,.55);font-size:17px;font-weight:700;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:transform .2s,background .2s;}
.kvt-nav-btn:hover{background:#C9A84C;color:#1A0030;border-color:#C9A84C;transform:scale(1.08);}
.kvt-strip{padding:28px clamp(20px,5vw,60px) 56px;display:flex;align-items:center;justify-content:space-between;gap:20px;flex-wrap:wrap;border-top:1px solid rgba(255,255,255,.05);position:relative;z-index:2;}
.kvt-ratings{display:flex;align-items:center;gap:28px;flex-wrap:wrap;}
.kvt-r-item{text-align:center;}
.kvt-r-num{font-family:'Playfair Display',serif;font-size:22px;font-weight:700;color:#C9A84C;line-height:1;}
.kvt-r-label{font-size:10px;color:rgba(255,255,255,.28);text-transform:uppercase;letter-spacing:1px;margin-top:4px;}
.kvt-r-sep{width:1px;height:36px;background:rgba(255,255,255,.06);}
.kvt-btn-gold{position:relative;overflow:hidden;background:linear-gradient(135deg,#B8860B,#C9A84C,#F5D87A,#C9A84C,#9A6E10);color:#1A0030;border:none;border-radius:50px;padding:13px 30px;font-weight:800;font-size:14px;cursor:pointer;display:inline-flex;align-items:center;gap:8px;font-family:'DM Sans',sans-serif;text-decoration:none;flex-shrink:0;transition:transform .25s,opacity .25s;}
.kvt-btn-gold::before{content:'';position:absolute;top:0;left:0;width:100%;height:100%;background:linear-gradient(105deg,transparent 35%,rgba(255,255,255,.35) 50%,transparent 65%);transform:translateX(-100%);animation:kvt-shine 3.5s ease infinite;pointer-events:none;}
.kvt-btn-gold:hover{transform:translateY(-2px);opacity:.92;color:#1A0030;}

/* ── Written Reviews ── */
.kt-sec{padding:100px 0 88px;background:#1A0030;position:relative;overflow:hidden;font-family:'DM Sans',sans-serif;}
.kt-sec::before{content:'';position:absolute;inset:0;background:radial-gradient(ellipse 70% 55% at 15% 5%,rgba(94,14,115,.32) 0%,transparent 65%),radial-gradient(ellipse 55% 45% at 85% 95%,rgba(201,168,76,.09) 0%,transparent 65%);pointer-events:none;}
.kt-sec::after{content:'';position:absolute;top:0;left:50%;transform:translateX(-50%);width:200px;height:2px;background:linear-gradient(90deg,transparent,#C9A84C,transparent);}
.kt-tag{display:inline-block;font-size:11px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:#C9A84C;margin-bottom:10px;}
.kt-h{font-family:'Playfair Display',serif;font-size:clamp(30px,4.5vw,52px);font-weight:700;line-height:1.15;color:#fff;margin:0;}
.kt-h span{color:#C9A84C;}
.kt-sub{color:rgba(255,255,255,.5);font-size:14px;line-height:1.75;margin:12px auto 0;max-width:500px;}
.kt-stats{display:flex;align-items:center;justify-content:center;gap:40px;margin-top:40px;flex-wrap:wrap;}
.kt-stat{text-align:center;}
.kt-sn{font-family:'Playfair Display',serif;font-size:34px;font-weight:700;color:#C9A84C;line-height:1;}
.kt-sl{font-size:10px;color:rgba(255,255,255,.4);letter-spacing:1.5px;text-transform:uppercase;margin-top:5px;}
.kt-ssep{width:1px;height:44px;background:rgba(255,255,255,.08);}
.kt-feat{border-radius:28px;overflow:hidden;width:100%;height:100%;background:rgba(255,255,255,.04);border:1px solid rgba(201,168,76,.22);position:relative;}
.kt-feat::before{content:'';display:block;width:100%;height:2px;background:linear-gradient(90deg,transparent,#C9A84C,transparent);}
.kt-feat-in{padding:30px 28px 26px;}
.kt-big-q{font-family:'Playfair Display',serif;font-size:130px;font-weight:900;line-height:.8;color:rgba(201,168,76,.055);position:absolute;top:20px;right:16px;user-select:none;pointer-events:none;}
.kt-fi-wrap{position:relative;width:100%;height:240px;overflow:hidden;border-radius:18px;margin-bottom:22px;}
.kt-fi{width:100%;height:100%;object-fit:cover;display:block;transition:transform .6s;}
.kt-feat:hover .kt-fi{transform:scale(1.04);}
.kt-fi-ovl{position:absolute;inset:0;background:linear-gradient(to top,rgba(26,0,48,.75) 0%,transparent 55%);}
.kt-fi-badge{position:absolute;bottom:14px;left:14px;background:rgba(201,168,76,.18);border:1px solid rgba(201,168,76,.4);color:#E8D5A0;font-size:9px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;padding:4px 12px;border-radius:50px;}
.kt-review{font-size:15px;color:rgba(255,255,255,.82);line-height:1.8;font-style:italic;margin:0 0 22px;}
.kt-div{height:1px;margin-bottom:18px;background:linear-gradient(90deg,transparent,rgba(201,168,76,.28),transparent);}
.kt-author{display:flex;align-items:center;gap:14px;}
.kt-avatar{width:52px;height:52px;border-radius:50%;object-fit:cover;flex-shrink:0;border:2.5px solid rgba(201,168,76,.45);}
.kt-aname{font-family:'Playfair Display',serif;font-size:16px;font-weight:700;color:#fff;}
.kt-aloc{font-size:11px;color:rgba(255,255,255,.4);text-transform:uppercase;letter-spacing:.5px;margin-top:2px;}
.kt-v{margin-left:auto;flex-shrink:0;width:30px;height:30px;border-radius:50%;background:linear-gradient(135deg,#5E0E73,#C9A84C);display:flex;align-items:center;justify-content:center;font-size:12px;color:#fff;font-weight:700;}
.kt-prev{background:rgba(255,255,255,.03);border:1px solid rgba(201,168,76,.08);border-radius:20px;overflow:hidden;cursor:pointer;transition:transform .3s;display:flex;flex-direction:column;flex:1;}
.kt-prev:hover{transform:translateY(-5px);background:rgba(255,255,255,.06);border-color:rgba(201,168,76,.28);}
.kt-pi{width:100%;height:155px;object-fit:cover;display:block;transition:transform .4s;filter:brightness(.8);}
.kt-prev:hover .kt-pi{transform:scale(1.04);filter:brightness(1);}
.kt-pb{padding:15px 17px;flex:1;display:flex;flex-direction:column;}
.kt-pt{font-size:12px;color:rgba(255,255,255,.55);line-height:1.65;font-style:italic;margin:8px 0 10px;flex:1;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;}
.kt-pn{font-size:13px;font-weight:700;color:rgba(255,255,255,.85);font-family:'Playfair Display',serif;}
.kt-pl{font-size:10px;color:rgba(255,255,255,.3);margin-top:2px;}
.kt-pr{display:inline-block;margin-top:10px;font-size:10px;font-weight:700;color:#C9A84C;letter-spacing:1px;text-transform:uppercase;padding:4px 12px;border:1px solid rgba(201,168,76,.25);border-radius:50px;background:transparent;width:fit-content;transition:background .2s;}
.kt-prev:hover .kt-pr{background:rgba(201,168,76,.12);border-color:rgba(201,168,76,.5);}
.kt-nav{display:flex;justify-content:center;align-items:center;gap:8px;margin-top:36px;}
.kt-dot{width:8px;height:8px;border-radius:50%;background:rgba(255,255,255,.15);border:none;cursor:pointer;transition:width .35s,background .35s;}
.kt-dot.on{width:34px;border-radius:4px;background:linear-gradient(90deg,#5E0E73,#C9A84C);}
.kt-arr{width:46px;height:46px;border-radius:50%;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.12);color:rgba(255,255,255,.6);font-size:20px;cursor:pointer;transition:transform .2s,background .2s;display:flex;align-items:center;justify-content:center;}
.kt-arr:hover{background:#C9A84C;color:#1A0030;border-color:#C9A84C;transform:scale(1.08);}
.kt-plat{display:flex;justify-content:center;align-items:center;gap:32px;flex-wrap:wrap;margin-top:60px;padding:26px 44px;background:rgba(255,255,255,.025);border:1px solid rgba(255,255,255,.055);border-radius:22px;}
.kt-plat-item{text-align:center;}
.kt-plat-name{font-size:10px;color:rgba(255,255,255,.3);letter-spacing:2px;text-transform:uppercase;margin-bottom:6px;}
.kt-plat-row{display:flex;align-items:center;gap:7px;}
.kt-plat-sc{font-family:'Playfair Display',serif;font-size:22px;font-weight:700;color:#fff;}
.kt-plat-cnt{font-size:10px;color:rgba(255,255,255,.25);margin-top:3px;}
.kt-psep{width:1px;height:40px;background:rgba(255,255,255,.07);}
.kt-cta{text-align:center;margin-top:60px;padding:52px 40px;background:linear-gradient(135deg,rgba(94,14,115,.15),rgba(26,0,48,.5));border:1px solid rgba(201,168,76,.1);border-radius:28px;position:relative;overflow:hidden;}
.kt-cta::before{content:'';position:absolute;top:0;left:50%;transform:translateX(-50%);width:120px;height:1px;background:linear-gradient(90deg,transparent,#C9A84C,transparent);}
.kt-cta-h{font-family:'Playfair Display',serif;font-size:clamp(22px,3vw,32px);font-weight:700;color:#fff;margin:0 0 8px;}
.kt-cta-h span{color:#C9A84C;}
.kt-cta-sub{font-size:14px;color:rgba(255,255,255,.4);margin-bottom:28px;}
.kt-cta-row{display:flex;gap:14px;justify-content:center;flex-wrap:wrap;}
.kt-btn-bk{background:linear-gradient(135deg,#5E0E73,#6E1A85);color:#fff;border:none;border-radius:50px;padding:14px 32px;font-weight:700;font-size:14px;cursor:pointer;text-decoration:none;display:inline-block;transition:transform .25s,opacity .25s;}
.kt-btn-bk:hover{transform:translateY(-2px);opacity:.9;color:#fff;}
.kt-btn-wa{background:#25D366;color:#fff;border:none;border-radius:50px;padding:14px 32px;font-weight:700;font-size:14px;cursor:pointer;text-decoration:none;display:inline-flex;align-items:center;gap:8px;transition:transform .25s,opacity .25s;}
.kt-btn-wa:hover{transform:translateY(-2px);opacity:.9;color:#fff;}

/* ── Responsive ── */
@media(max-width:1024px){
  .kvt-stage{grid-template-columns:1fr 280px;}
  .kvt-header,.kvt-strip{padding-left:32px;padding-right:32px;}
  .kvt-pane{padding-left:40px;}
  .svc-grid{grid-template-columns:repeat(2,1fr);gap:16px;}
}
/* ══ MOBILE HERO — video-first, full-screen ══════════════════════ */
@media(max-width:768px){

  /* Hero: stack video on top, text below — video is the star */
  .kp-hero{min-height:100svh;align-items:flex-start;overflow-x:clip;}
  .kp-hero-cnt{padding:0!important;width:100%;overflow:hidden;}

  /* Neutralize Bootstrap row negative margins that cause left-clip */
  .kp-hero .row{flex-direction:column-reverse!important;gap:0!important;margin-left:0!important;margin-right:0!important;}

  /* VIDEO COL — full width, tall */
  .kp-hero .col-lg-6:last-child{
    padding:0!important;
    width:100%!important;
    max-width:100%!important;
    flex:none!important;
  }
  .kp-hero-vid-wrap{
    width:100%!important;
    max-height:none!important;
    height:72svh!important;
    border-radius:0!important;
    border:none!important;
    border-bottom:2px solid rgba(201,168,76,.3)!important;
    aspect-ratio:unset!important;
  }

  /* TEXT COL — compact strip below video */
  .kp-hero .col-lg-6:first-child{
    width:100%!important;
    max-width:100%!important;
    flex:none!important;
    padding:20px 20px 24px!important;
    box-sizing:border-box!important;
  }
  .kp-hero-badge{font-size:10px;padding:6px 14px;margin-bottom:10px;}
  .kp-hero-title{font-size:clamp(24px,6.5vw,34px)!important;margin-bottom:10px;line-height:1.1;}
  .kp-hero-sub{display:none!important;}           /* hide subtitle — video shows it */
  .kp-trust-row{display:none!important;}           /* hide stats — cleaner on mobile */
  .kp-hero-float{display:none!important;}

  /* Buttons — side by side, compact */
  .kp-hero .col-lg-6:first-child>div[style]{flex-wrap:nowrap!important;gap:10px!important;}
  .kp-hero-cnt .cc-btn-gold{flex:1;justify-content:center;padding:12px 14px!important;font-size:13px!important;}
  .kp-hero-cnt .cc-btn-outline{flex:1;justify-content:center;padding:11px 14px!important;font-size:13px!important;}

  /* Section spacing */
  .kp-sec{padding:52px 0;}
  .kp-sec-sm{padding:28px 0;}

  /* Themes */
  .theme-card{width:200px;}.theme-img{height:240px;}

  /* Gallery */
  .gal-bento{grid-template-columns:repeat(6,1fr);gap:10px;}
  .gbi-1{grid-column:span 3;grid-row:span 2;min-height:250px;}
  .gbi-2,.gbi-3{grid-column:span 3;min-height:120px;}
  .gbi-4,.gbi-5,.gbi-6{grid-column:span 2;min-height:135px;}

  /* Enquiry form */
  .enq-wrap .row{flex-direction:column;}
  .enq-left{order:2;padding:26px 22px;}
  .enq-right{order:1;padding:26px 22px;}

  /* Reels section */
  .kr-sec{padding:70px 0 80px;}
  .kr-header{margin-bottom:36px;}
  .kr-title{font-size:clamp(28px,7vw,38px);}

  /* Video testimonials */
  .kvt-stage{grid-template-columns:1fr;grid-template-rows:auto auto;}
  .kvt-pane{padding:32px 20px;min-height:480px;}
  .kvt-reel{width:min(250px,74%);}
  .kvt-queue{border-left:none;border-top:1px solid rgba(255,255,255,.06);max-height:none;}
  .kvt-queue-header{padding:14px 20px 10px;}
  .kvt-queue-list{display:flex;flex-direction:row;overflow-x:auto;overflow-y:hidden;padding:10px 16px 14px;gap:10px;-webkit-overflow-scrolling:touch;}
  .kvt-queue-list::-webkit-scrollbar{display:none;}
  .kvt-queue-nav{padding:12px 20px;}
  .kvt-q-item{flex-direction:column;align-items:center;text-align:center;min-width:84px;border-left:none;border-bottom:3px solid transparent;padding:8px;border-radius:12px;}
  .kvt-q-item.active{border-bottom-color:#C9A84C;border-left-color:transparent;}
  .kvt-q-thumb{width:50px;height:76px;}
  .kvt-q-num{display:none;}
  .kvt-header{padding:40px 20px 32px;}
  .kvt-header::after{left:20px;right:20px;}
  .kvt-strip{padding:20px 20px 36px;flex-direction:column;align-items:flex-start;}
  .kvt-r-sep{display:none;}

  /* Written reviews */
  .kt-sec{padding:64px 0 56px;}
  .kt-fi-wrap{height:200px;}
  .kt-ssep{display:none;}
  .kt-plat{gap:20px;padding:20px 24px;}
  .kt-psep{display:none;}
}

/* ══ 640px — phones ══════════════════════════════════════════════ */
@media(max-width:640px){
  .kp-hero-vid-wrap{height:75svh!important;}
  .kp-hero .col-lg-6:first-child{padding:16px 16px 20px!important;}
  .kp-hero-title{font-size:clamp(22px,6.5vw,28px)!important;}

  .kp-sec{padding:44px 0;}
  .container{padding-left:16px;padding-right:16px;}
  .cc-h{font-size:clamp(22px,6.5vw,30px);}
  .cc-btn,.cc-btn-gold,.cc-btn-outline,.wa-btn{min-height:46px;font-size:13px;}
  .theme-card{width:170px;}.theme-img{height:210px;}
  .svc-grid{grid-template-columns:1fr 1fr;gap:10px;}
  .svc-img-wrap{height:130px;}
  .kr-card{height:420px;}.kr-info h3{font-size:15px;}.kr-nav-btn{width:40px;height:40px;font-size:17px;}
  .gal-bento{grid-template-columns:1fr 1fr;gap:8px;}
  .gbi-1{grid-column:span 2;min-height:175px;grid-row:span 1;}
  .gbi-2,.gbi-3,.gbi-4,.gbi-5{grid-column:span 1;min-height:125px;}
  .gbi-6{grid-column:span 2;min-height:145px;}
  .enq-left,.enq-right{padding:18px 14px;}
  .enq-input{padding:11px 13px;font-size:13px;}
  .kp-faq-q{font-size:13px;padding:12px 16px;}
  .kp-faq-a{font-size:12px;padding:0 16px 12px;}
  .kvt-h{font-size:clamp(24px,7vw,32px);}
  .kt-feat-in{padding:20px 18px;}
  .kt-fi-wrap{height:170px;}
  .kt-review{font-size:13px;}
  .kt-cta{padding:32px 18px;}
  .kt-plat{padding:18px 16px;gap:16px;}

  /* Reel cards on small phones */
  .kvt-reel{width:min(220px,80%);}
}

/* ══ 400px — small/older phones ═════════════════════════════════ */
@media(max-width:400px){
  .kp-hero-vid-wrap{height:78svh!important;}
  .kp-hero-title{font-size:clamp(20px,6.5vw,24px)!important;}
  .cc-h{font-size:clamp(20px,7vw,26px);}
  .svc-grid{grid-template-columns:1fr;}
  .theme-card{width:150px;}
}
@media(hover:none) and (pointer:coarse){
  .theme-card:hover,.svc-card:hover,.act-card:hover,.kr-card:hover,.kt-prev:hover,.gal-bento-item:hover{transform:none!important;}
  .svc-card:hover::before,.act-card:hover::before,.gal-bento-item:hover::before{opacity:0!important;}
  .svc-card:hover .svc-img,.theme-card:hover .theme-img,.gal-bento-item:hover img{transform:none!important;}
  .svc-card:hover .svc-glow,.gal-bento-item:hover .gal-bento-ovl{opacity:0!important;}
  .kp-hero-float{animation:none!important;display:none!important;}
  .kp-ticker-track{animation-duration:40s!important;}
}
`;

/* ─── Static data ────────────────────────────────────────────────── */
// const KID_THEMES=[
//   {name:"Dinosaur Adventure",bookings:"487",tags:["Dinos","Jungle"],img:"https://images.unsplash.com/photo-1506880018603-83d5b814b5a6?w=600&q=80",emoji:"🦕"},
//   {name:"Princess Royal",bookings:"562",tags:["Princess","Castle"],img:"https://plus.unsplash.com/premium_photo-1664205766214-9e3f453ef4cc?q=80",emoji:"👑"},
//   {name:"Space Explorer",bookings:"398",tags:["Space","Galaxy"],img:"https://images.unsplash.com/photo-1614728263952-84ea256f9679?w=600&q=80",emoji:"🚀"},
//   {name:"Underwater World",bookings:"312",tags:["Ocean","Fish"],img:"https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=600&q=80",emoji:"🐠"},
//   {name:"Superhero Academy",bookings:"445",tags:["Hero","Action"],img:"https://images.unsplash.com/photo-1612036782180-6f0b6cd846fe?w=600&q=80",emoji:"⚡"},
//   {name:"Candyland",bookings:"534",tags:["Sweet","Candy"],img:"https://images.unsplash.com/photo-1582058091505-f87a2e55a40f?w=600&q=80",emoji:"🍭"},
// ];
const KID_THEMES = [
  { name: "Joy Theatre", bookings: "189 bookings", tags: ["Cozy", "Stars", "Kids Fav"], img: "https://api.carnivalcastle.com/uploads/theatreImg/1777372400882-DSC_0915.JPG" },
  { name: "Royal Theatre", bookings: "234 bookings", tags: ["Grand", "Luxury", "Dolby"], img: "https://api.carnivalcastle.com/uploads/theatreImg/1774414279320-1000177805.webp" },
  { name: "Amora Theatre", bookings: "158 bookings", tags: ["Romantic", "Intimate", "Warm"], img: "https://api.carnivalcastle.com/uploads/theatreImg/1760769573708-1000416683.jpg" },
  { name: "Iris Theatre", bookings: "198 bookings", tags: ["Art Deco", "Neon", "Elegant"], img: "https://api.carnivalcastle.com/uploads/theatreImg/1773393489029-img1.jpeg" },
  { name: "Galaxy Theatre", bookings: "221 bookings", tags: ["Bright", "Spacious", "Surround"], img: "https://api.carnivalcastle.com/uploads/theatreImg/1727807868995-WhatsApp%20Image%202024-07-14%20at%2014.27.54%20(1).jpeg" },
  { name: "Bloom Theatre", bookings: "305 bookings", tags: ["Floral", "Whimsical", "Grand"], img: "https://api.carnivalcastle.com/uploads/theatreImg/1760770113682-1000416699.jpg" },
  { name: "Couple Cove Theatre", bookings: "167 bookings", tags: ["Private", "Candlelit", "Luxe"], img: "https://api.carnivalcastle.com/uploads/theatreImg/1727168553419-preview%20(1).jpg" },
  { name: "Lilac Theatre", bookings: "189 bookings", tags: ["Pastel", "Celebration", "Cute"], img: "https://api.carnivalcastle.com/uploads/theatreImg/1760768949493-1000416676.jpg" },
  { name: "Radiant Theatre", bookings: "173 bookings", tags: ["Warm Glow", "Acoustic", "Modern"], img: "https://api.carnivalcastle.com/uploads/theatreImg/1760770393852-1000416707.png" },
  { name: "Cadbury", bookings: "198 bookings", tags: ["Chocolate Theme", "Premium", "VIP"], img: "https://api.carnivalcastle.com/uploads/theatreImg/1760703722427-1000399554.jpg" },
  { name: "Lyra", bookings: "198 bookings", tags: ["Glamorous", "Classic", "Red Carpet"], img: "https://api.carnivalcastle.com/uploads/theatreImg/1760768920302-1000416675.jpg" },
  { name: "Carnival Den", bookings: "210 bookings", tags: ["Carnival", "Fun", "Festive"], img: "https://api.carnivalcastle.com/uploads/theatreImg/1765449525498-WhatsApp%20Image%202025-12-10%20at%205.56.04%20PM.jpeg" },
  { name: "Vibe Theatre", bookings: "195 bookings", tags: ["Vibrant", "Energetic", "Trendy"], img: "https://api.carnivalcastle.com/uploads/theatreImg/1790490082697-1000652413.jpg" },
];

const KID_ACTIVITIES=[
  {name:"Movie Screening",dur:"30 mins",age:"3–12",img:"/assets/img/theater.jpg"},
  {name:"Balloon Arch & Play",dur:"45 mins",age:"All ages",img:"https://api.carnivalcastle.com/uploads/theatreImg/1760770113682-1000416699.jpg"},
  {name:"Party Games & Props",dur:"1 hour",age:"3–12",img:"https://api.carnivalcastle.com/uploads/theatreImg/1765449525498-WhatsApp%20Image%202025-12-10%20at%205.56.04%20PM.jpeg"},
  {name:"Special Celebration",dur:"30 mins",age:"All ages",img:"https://api.carnivalcastle.com/uploads/theatreImg/1777372400882-DSC_0915.JPG"},
];
const SERVICE_CARDS=[
  {label:"Themed Decor",img:"https://api.carnivalcastle.com/uploads/theatreImg/1774414279320-1000177805.webp",fallback:"/assets/img/theater.jpg",desc:"Complete themed decor — balloons, backdrops, props & more."},
  {label:"Custom Cakes",img:"/img/c4.jpg",fallback:"/img/c2.jpg",desc:"Delicious custom celebration cakes crafted by master bakers."},
  {label:"Food & Snacks",img:"/img/foodbeverages.jpg",fallback:"/img/foodbeverages.jpg",desc:"Kid-friendly menus with vegetarian & delicious party snack options."},
  {label:"Photography",img:"https://api.carnivalcastle.com/uploads/galleryImg/1774802984680-WhatsApp%20Image%202026-03-29%20at%2010.19.07%20PM.jpeg",fallback:"https://api.carnivalcastle.com/uploads/galleryImg/1774802474665-WhatsApp%20Image%202026-03-29%20at%2010.10.46%20PM.jpeg",desc:"Capture every precious star moment with professional 4K candid coverage."},
];
const REELS=[
  {views:"487K",title:"Birthday Celebration",tag:"Iris Decor",video:mediaUrl(MEDIA.occasions.kidsBirthday) || "/assets/videos/Kids_Birthday.mp4",fallback:"/assets/videos/Kids_Birthday.mp4"},
  {views:"312K",title:"Birthday Celebration",tag:"Kids Car Entry",video:mediaUrl(MEDIA.occasions.carEntry) || "/assets/videos/Car_Entry.mp4",fallback:"/assets/videos/Car_Entry.mp4"},
  {views:"534K",title:"Birthday Celebration",tag:"Iris Decor",video:mediaUrl(MEDIA.occasions.privateTheatres) || "/assets/videos/Private_Theatres.mp4",fallback:"/assets/videos/Private_Theatres.mp4"},
  {views:"445K",title:"Birthday Celebration",tag:"Jungle Theme",video:mediaUrl(MEDIA.occasions.ayanshBirthday) || "/assets/videos/Ayansh_Birthday.mp4",fallback:"/assets/videos/Ayansh_Birthday.mp4"},
  {views:"398K",title:"Birthday Celebration",tag:"Jungle Theme",video:mediaUrl(MEDIA.occasions.littleBundleOfJoy) || "/assets/videos/Little_bundle_of_joy_Svojass.mp4",fallback:"/assets/videos/Little_bundle_of_joy_Svojass.mp4"},
  {views:"562K",title:"Birthday Celebration",tag:"Jungle Theme",video:mediaUrl(MEDIA.occasions.angelDhanvika) || "/assets/videos/Angel_Dhanvika.mp4",fallback:"/assets/videos/Angel_Dhanvika.mp4"},
];

const VID_TESTI = [
  {
    src: mediaUrl(MEDIA.occasions.kidsBirthday) || "/assets/videos/Kids_Birthday.mp4",
    fallback: "/assets/videos/Kids_Birthday.mp4",
    name: "Priya & Family",
    event: "Princess Celebration",
    loc: "Banjara Hills",
    quote: "The private theatre setup was out of this world. Our daughter was dancing with joy!",
    rating: 5,
  },
  {
    src: mediaUrl(MEDIA.occasions.ayanshBirthday) || "/assets/videos/Ayansh_Birthday.mp4",
    fallback: "/assets/videos/Ayansh_Birthday.mp4",
    name: "Karan Mehta",
    event: "Kids Birthday Party",
    loc: "Jubilee Hills",
    quote: "Every child was completely thrilled. The big screen visuals and sound were magical.",
    rating: 5,
  },
  {
    src: mediaUrl(MEDIA.occasions.littleBundleOfJoy) || "/assets/videos/Little_bundle_of_joy_Svojass.mp4",
    fallback: "/assets/videos/Little_bundle_of_joy_Svojass.mp4",
    name: "Arjun & Deepa",
    event: "Joy Celebration",
    loc: "Kondapur",
    quote: "Our family had the best time! The custom decor and attentive service were top-tier.",
    rating: 5,
  },
  {
    src: mediaUrl(MEDIA.occasions.angelDhanvika) || "/assets/videos/Angel_Dhanvika.mp4",
    fallback: "/assets/videos/Angel_Dhanvika.mp4",
    name: "Sruthi & Rajesh",
    event: "Angel Milestone",
    loc: "Madhapur",
    quote: "Unforgettable celebration! The photography captured memories we will cherish forever.",
    rating: 5,
  },
  {
    src: mediaUrl(MEDIA.occasions.carEntry) || "/assets/videos/Car_Entry.mp4",
    fallback: "/assets/videos/Car_Entry.mp4",
    name: "Vikram Reddy",
    event: "VIP Surprise",
    loc: "Gachibowli",
    quote: "The grand kids entry had everyone cheering! Absolutely worth every rupee.",
    rating: 5,
  },
  {
    src: mediaUrl(MEDIA.occasions.privateTheatres) || "/assets/videos/Private_Theatres.mp4",
    fallback: "/assets/videos/Private_Theatres.mp4",
    name: "Sunita Rao",
    event: "Private Theatre Bash",
    loc: "Kukatpally",
    quote: "Carnival Castle is our go-to celebration venue in Hyderabad. Outstanding quality!",
    rating: 5,
  },
];
const REVIEWS=[
  {name:"Priya & Arjun",location:"Banjara Hills",rating:5,occasion:"Princess Royal Theme",text:"My daughter's 5th birthday was absolutely magical! The princess theme was so detailed — from the crown décor to the pink fog entry. She was in tears of joy.",image:"https://images.unsplash.com/photo-1535556114965-a5dfc3cda7b9?w=800&q=80",avatar:"https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&q=80"},
  {name:"Rahul Mehta",location:"Jubilee Hills",rating:5,occasion:"Superhero Academy",text:"Booked the Superhero theme for my son's birthday and every child was completely blown away. The costumes, the activities, the cake — all perfect.",image:"https://images.unsplash.com/photo-1612036782180-6f0b6cd846fe?w=800&q=80",avatar:"https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&q=80"},
  {name:"Sunita Varma",location:"Madhapur",rating:5,occasion:"Dinosaur Adventure",text:"The Dinosaur theme was beyond imagination. Kids were running around in awe, the activities were age-perfect and the photographer captured every precious moment.",image:"https://images.unsplash.com/photo-1506880018603-83d5b814b5a6?w=800&q=80",avatar:"https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=120&q=80"},
  {name:"Vikram Desai",location:"Kondapur",rating:5,occasion:"Candyland Theme",text:"Candyland for my daughter's 7th birthday — she still talks about it every week! The attention to detail in the sweet-themed décor and the custom cake was incredible.",image:"https://images.unsplash.com/photo-1582058091505-f87a2e55a40f?w=800&q=80",avatar:"https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&q=80"},
  {name:"Kavitha Nair",location:"Gachibowli",rating:5,occasion:"Space Explorer",text:"Space Explorer theme exceeded every expectation. The galaxy décor, the activities, the glow-in-dark props — the kids felt like real astronauts.",image:"https://images.unsplash.com/photo-1614728263952-84ea256f9679?w=800&q=80",avatar:"https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&q=80"},
  {name:"Sanjay Pillai",location:"Kukatpally",rating:5,occasion:"Royal Celebration",text:"The Royal Celebration package for my son's 8th birthday — the magic show, goodie bags, 3-tier custom cake. Every detail handled with care.",image:"https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=800&q=80",avatar:"https://images.unsplash.com/photo-1552058544-f2b08422138a?w=120&q=80"},
];
const KID_GALLERY=["/assets/img/m1.jpg","/assets/img/m2.jpg","/assets/img/m3.jpg","/assets/img/m4.jpg","/assets/img/m5.jpg","/assets/img/m6.jpg"];

/* ─── Helpers ────────────────────────────────────────────────────── */
function Stars({rating=5,size=13}){
  return <div style={{display:"flex",gap:2}}>{[1,2,3,4,5].map(n=><span key={n} style={{fontSize:size,color:n<=rating?C.gold:"rgba(201,168,76,.2)"}}>★</span>)}</div>;
}

/* ─── Isolated memo sub-components ──────────────────────────────── */
const HeroVideo = memo(() => {
  const r = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    if (r.current) {
      r.current.muted = true;
      r.current.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  }, []);

  const togglePlay = () => {
    if (!r.current) return;
    if (r.current.paused) {
      r.current.muted = false;
      setMuted(false);
      r.current.play().then(() => setPlaying(true)).catch(() => {});
    } else if (muted) {
      r.current.muted = false;
      setMuted(false);
    } else {
      r.current.pause();
      setPlaying(false);
    }
  };

  return (
    <div className="kp-hero-vid-wrap" onClick={togglePlay} style={{ cursor: "pointer" }}>
      <video
        ref={r}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        src={mediaUrl(MEDIA.occasions.kidsBirthday) || "/assets/videos/Kids_Birthday.mp4"}
        poster="https://api.carnivalcastle.com/uploads/theatreImg/1777372400882-DSC_0915.JPG"
        muted={muted}
        autoPlay
        loop
        playsInline
        preload="auto"
        onError={(e) => {
          if (e.target.src !== "/assets/videos/Kids_Birthday.mp4") {
            e.target.src = "/assets/videos/Kids_Birthday.mp4";
          }
        }}
      />
      <div className="kp-hero-vid-ovl" />
      {(!playing || muted) && (
        <div className="cc-play-button-wrapper">
          <div className="cc-play-pulse" />
          <button className="cc-play-btn" aria-label="Play">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
          </button>
        </div>
      )}
      <div className="kp-hero-vid-badge">
        <div className="kp-hero-vid-pulse" />
        <span className="kp-hero-vid-label">🎈 Real Kids Birthday Celebrations — Watch the Magic</span>
        {muted && <span style={{ marginLeft: "auto", fontSize: 10, color: "rgba(255,255,255,0.75)" }}>Tap to Unmute 🔊</span>}
      </div>
    </div>
  );
});

const ThemeCarousel=memo(({navigate})=>{
  const [idx,setIdx]=useState(0);
  useEffect(()=>{const t=setInterval(()=>setIdx(i=>(i+1)%KID_THEMES.length),3500);return()=>clearInterval(t);},[]);
  return(
    <section className="kp-sec" style={{background:C.dark,overflow:"hidden"}}>
      <div className="container">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:44,flexWrap:"wrap",gap:20}}>
          <div><span className="cc-tag cc-tag-light">✦ 15+ Dream Themes ✦</span><h2 className="cc-h cc-h-white">Your Child's <span className="gold">Fantasy</span> Celebrations Decor</h2></div>
          <div style={{display:"flex",gap:10}}>
            <button className="cc-nav-btn" onClick={()=>setIdx(i=>(i-1+KID_THEMES.length)%KID_THEMES.length)}>‹</button>
            <button className="cc-nav-btn" onClick={()=>setIdx(i=>(i+1)%KID_THEMES.length)}>›</button>
          </div>
        </div>
        <div style={{overflow:"hidden"}}>
          <div style={{display:"flex",gap:18,transition:"transform .7s cubic-bezier(.25,1,.5,1)",transform:`translateX(calc(-${idx*(280+18)}px))`}}>
            {[...KID_THEMES,...KID_THEMES].map((t,i)=>(
              <div key={i} className="theme-card" onClick={()=>navigate("/locations")}>
                <div className="kp-emoji-badge">{t.emoji}</div>
                <img
                  src={t.img}
                  alt={t.name}
                  className="theme-img"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.src = "https://api.carnivalcastle.com/uploads/theatreImg/1774414279320-1000177805.webp";
                  }}
                />
                <div className="theme-ovl">
                  <div className="theme-name">{t.name}</div>
                  {/* <div className="theme-booked">🔥 {t.bookings} booked</div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:8}}>{t.tags.map((tg,j)=><span key={j} className="theme-tag">{tg}</span>)}</div> */}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{display:"flex",gap:8,justifyContent:"center",marginTop:26}}>
          {KID_THEMES.map((_,i)=><button key={i} onClick={()=>setIdx(i)} style={{width:i===idx?26:8,height:8,borderRadius:8,background:i===idx?C.gold:"rgba(255,255,255,.2)",border:"none",cursor:"pointer",transition:"all .4s"}}/>)}
        </div>
      </div>
    </section>
  );
});

const ReelsSection=memo(()=>{
  const [idx,setIdx]=useState(0);
  const [hovered,setHovered]=useState(false);
  const [vis,setVis]=useState(4);
  const tRef=useRef(null);
  useEffect(()=>{const u=()=>{const w=window.innerWidth;setVis(w<540?1:w<768?2:w<1100?3:4);};u();window.addEventListener("resize",u);return()=>window.removeEventListener("resize",u);},[]);
  const max=REELS.length-vis;
  const start=useCallback(()=>{clearInterval(tRef.current);tRef.current=setInterval(()=>setIdx(i=>i>=max?0:i+1),3000);},[max]);
  useEffect(()=>{if(!hovered)start();else clearInterval(tRef.current);return()=>clearInterval(tRef.current);},[hovered,start]);
  const IGIcon=()=><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>;
  return(
    <section className="kr-sec">
      <div className="kr-orb" style={{width:460,height:460,background:"radial-gradient(circle,rgba(94,14,115,.28) 0%,transparent 70%)",top:-80,left:-80}}/>
      <div className="kr-orb" style={{width:320,height:320,background:"radial-gradient(circle,rgba(201,168,76,.1) 0%,transparent 70%)",bottom:-60,right:-40}}/>
      <div className="kr-container">
        <div className="kr-header">
          <div>
            <div className="kr-eyebrow"><div className="kr-eyebrow-line"/>Party Reels<div className="kr-eyebrow-line"/></div>
            <h2 className="kr-title">Watch the <em>Magic</em><br/>Happen Live</h2>
            <p className="kr-sub">Real parties. Real smiles. Real unforgettable moments from our little guests.</p>
          </div>
          <div className="kr-nav">
            <button className="kr-nav-btn" onClick={()=>{setIdx(i=>Math.max(0,i-1));start();}}>‹</button>
            <button className="kr-nav-btn" onClick={()=>{setIdx(i=>i>=max?0:i+1);start();}}>›</button>
          </div>
        </div>
        <div className="kr-wrap" onMouseEnter={()=>setHovered(true)} onMouseLeave={()=>setHovered(false)}>
          <div className="kr-track" style={{transform:`translateX(${-(idx*280)}px)`}}>
            {REELS.map((r,i)=>(
              <div key={i} className="kr-card">
                <video
                  className="kr-video"
                  src={r.video}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="none"
                  onError={(e) => {
                    if (r.fallback && e.target.src !== r.fallback) {
                      e.target.src = r.fallback;
                    }
                  }}
                />
                <div className="kr-ovl"/>
                <div className="kr-views">👁 {r.views}</div>
                <div className="kr-play">▶</div>
                <div className="kr-info"><div className="kr-tag-pill">{r.tag}</div><h3>{r.title}</h3></div>
              </div>
            ))}
          </div>
        </div>
        <div className="kr-dots">
          {Array.from({length:REELS.length-vis+1}).map((_,i)=>(
            <button key={i} className={`kr-dot${i===idx?" active":""}`} style={{width:i===idx?28:8}} onClick={()=>{setIdx(i);start();}}/>
          ))}
        </div>
        <div className="kr-cta">
          <a href="https://www.instagram.com/carnival_castle_/" target="_blank" rel="noopener noreferrer" className="kr-btn-ig"><IGIcon/> Follow @carnivalcastle</a>
          <a href="https://wa.me/918374777834" target="_blank" rel="noopener noreferrer" className="kr-btn-gold">🎈 Book Your Party</a>
        </div>
      </div>
    </section>
  );
});

const VideoTestimonials=memo(()=>{
  const [ai,setAi]=useState(0);
  const [playing,setPlaying]=useState(false);
  const [progress,setProgress]=useState(0);
  const [trans,setTrans]=useState(false);
  const [showCtrl,setShowCtrl]=useState(false);
  const vRef=useRef(null),sRef=useRef(null),aRef=useRef(null),cRef=useRef(null);
  const cur=VID_TESTI[ai];
  /* stop when scrolled out */
  useEffect(()=>{const el=sRef.current;if(!el)return;const o=new IntersectionObserver(([e])=>{if(!e.isIntersecting){vRef.current?.pause();setPlaying(false);}},{threshold:.25});o.observe(el);return()=>o.disconnect();},[]);
  /* reset on index change */
  useEffect(()=>{const v=vRef.current;if(!v)return;v.pause();v.currentTime=0;v.muted=false;setPlaying(false);setProgress(0);},[ai]);
  useEffect(()=>{const v=vRef.current;if(!v)return;const t=()=>{if(v.duration)setProgress((v.currentTime/v.duration)*100);};const e=()=>{setPlaying(false);setProgress(0);};v.addEventListener("timeupdate",t);v.addEventListener("ended",e);return()=>{v.removeEventListener("timeupdate",t);v.removeEventListener("ended",e);};},[ai]);
  const sc=()=>{setShowCtrl(true);clearTimeout(cRef.current);cRef.current=setTimeout(()=>setShowCtrl(false),2500);};
  const isTouchDevice=()=>window.matchMedia("(hover:none) and (pointer:coarse)").matches;
  const onEnter=()=>{if(isTouchDevice())return;sc();const v=vRef.current;if(v&&!playing){v.muted=false;v.play().then(()=>setPlaying(true)).catch(()=>{});}}; 
  const onLeave=()=>{if(isTouchDevice())return;setShowCtrl(false);clearTimeout(cRef.current);if(vRef.current&&playing){vRef.current.pause();setPlaying(false);}};
  const toggleVideo=()=>{const v=vRef.current;if(!v)return;if(playing){v.pause();v.muted=false;setPlaying(false);}else{v.muted=false;v.play().then(()=>setPlaying(true)).catch(()=>{});}};
  const onReelClick=()=>{sc();toggleVideo();};
  const tog=e=>{e.stopPropagation();toggleVideo();};
  const goTo=useCallback(i=>{if(i===ai||trans)return;setTrans(true);setTimeout(()=>{setAi(i);setTrans(false);},300);},[ai,trans]);
  const goPrev = useCallback((e) => {
    if (e) e.stopPropagation();
    setTrans(true);
    setTimeout(() => {
      setAi(i => (i - 1 + 5) % 5);
      setTrans(false);
    }, 280);
  }, []);
  const goNext = useCallback((e) => {
    if (e) e.stopPropagation();
    setTrans(true);
    setTimeout(() => {
      setAi(i => (i + 1) % 5);
      setTrans(false);
    }, 280);
  }, []);
  useEffect(()=>{if(!playing){aRef.current=setInterval(()=>setAi(i=>(i+1)%VID_TESTI.length),5000);}else{clearInterval(aRef.current);}return()=>clearInterval(aRef.current);},[playing]);
  return(
    <section className="kvt-sec" ref={sRef}>
      <div className="kvt-header">
        <div><div className="kvt-eyebrow"><div className="kvt-eyebrow-line"/>Parent Stories<div className="kvt-eyebrow-line"/></div><h2 className="kvt-h">Real Parents,<br/><em>Real Happy Kids</em></h2></div>
        <div className="kvt-counter"><span>{String(ai+1).padStart(2,"0")}</span>{" / "+String(VID_TESTI.length).padStart(2,"0")}</div>
      </div>
      <div className="kvt-stage">
        <div className={`kvt-transition${trans?" show":""}`}/>
        <div className="kvt-pane">
          <div className={`kvt-glow${playing?" active":""}`}/>
          <button className="kvt-side-arr prev" onClick={goPrev} aria-label="Previous">‹</button>
          <div className={`kvt-reel${playing?" playing":""}`} onMouseEnter={onEnter} onMouseLeave={onLeave} onMouseMove={sc} onClick={onReelClick}>
            <div className="kvt-notch"/>
            <div className="kvt-prog"><div className="kvt-prog-fill" style={{width:`${progress}%`}}/></div>
            <video
              key={ai}
              ref={vRef}
              className="kvt-video"
              src={cur.src}
              muted={false}
              playsInline
              preload="metadata"
              onError={(e) => {
                if (cur.fallback && e.target.src !== cur.fallback) {
                  e.target.src = cur.fallback;
                }
              }}
            />
            <div className="kvt-ovl"/>
            {!playing && (
              <div className="cc-play-button-wrapper" onClick={tog}>
                <div className="cc-play-pulse" />
                <button className="cc-play-btn" aria-label="Play">
                  <svg width="24" height="24" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                </button>
              </div>
            )}
            {/* Info — only stars + name */}
            <div className="kvt-info" key={ai}>
              <div className="kvt-stars">{[1,2,3,4,5].map(n=><span key={n} style={{fontSize:15,color:n<=cur.rating?C.gold:"rgba(201,168,76,.2)"}}>★</span>)}</div>
              <div className="kvt-identity">
                <div className="kvt-avatar">{cur.name.split(" ").map(w=>w[0]).slice(0,2).join("")}</div>
                <div><div className="kvt-name">{cur.name}</div></div>
              </div>
            </div>
          </div>
          <button className="kvt-side-arr next" onClick={goNext} aria-label="Next">›</button>
        </div>
        <div className="kvt-queue">
          <div className="kvt-queue-header"><div className="kvt-queue-title">Up Next</div></div>
          <div className="kvt-queue-list">
            {VID_TESTI.map((t,i)=>(
              <div key={i} className={`kvt-q-item${i===ai?" active":""}`} onClick={()=>goTo(i)}>
                <div className="kvt-q-thumb"><div className="kvt-q-bg"/><span className="kvt-q-init">{t.name.split(" ").map(w=>w[0]).slice(0,2).join("")}</span>
                  {i===ai&&playing&&<div className="kvt-q-eq-wrap"><div className="kvt-q-eq"><div className="kvt-q-bar"/><div className="kvt-q-bar"/><div className="kvt-q-bar"/></div></div>}
                </div>
                <div className="kvt-q-info"><div className="kvt-q-name">{t.name}</div><div className="kvt-q-event">{t.event} · {t.loc}</div></div>
                <div className="kvt-q-num">{String(i+1).padStart(2,"0")}</div>
              </div>
            ))}
          </div>
          <div className="kvt-queue-nav">
            <span className="kvt-nav-label">Navigate</span>
            <div className="kvt-nav-arrows">
              <button className="kvt-nav-btn" onClick={()=>goTo((ai-1+VID_TESTI.length)%VID_TESTI.length)}>‹</button>
              <button className="kvt-nav-btn" onClick={()=>goTo((ai+1)%VID_TESTI.length)}>›</button>
            </div>
          </div>
        </div>
      </div>
      <div className="kvt-strip">
        <div className="kvt-ratings">
          {[{num:"4.9★",label:"Google"},{num:"4.8★",label:"Facebook"},{num:"2000+",label:"Parties"},{num:"99%",label:"Happy Parents"}].map((item,i,arr)=>(
            <React.Fragment key={i}>{i>0&&<div className="kvt-r-sep"/>}<div className="kvt-r-item"><div className="kvt-r-num">{item.num}</div><div className="kvt-r-label">{item.label}</div></div></React.Fragment>
          ))}
        </div>
        <button onClick={() => window.open("https://wa.me/918341428342?text=Hi%2C%20I%20would%20like%20to%20book%20a%20Kids%20Birthday%20Celebration%20at%20Carnival%20Castle!", "_blank")} className="kvt-btn-gold">🎈 Book Your Party</button>
      </div>
    </section>
  );
});

const WrittenReviews=memo(()=>{
  const [ai,setAi]=useState(0);
  const aRef=useRef(null);
  const start=()=>{clearInterval(aRef.current);aRef.current=setInterval(()=>setAi(i=>(i+1)%REVIEWS.length),4800);};
  useEffect(()=>{start();return()=>clearInterval(aRef.current);},[]);
  const goTo=i=>{setAi((i+REVIEWS.length)%REVIEWS.length);clearInterval(aRef.current);start();};
  const cur=REVIEWS[ai];
  const prev=REVIEWS[(ai-1+REVIEWS.length)%REVIEWS.length];
  const next=REVIEWS[(ai+1)%REVIEWS.length];
  const avg=(REVIEWS.reduce((s,r)=>s+r.rating,0)/REVIEWS.length).toFixed(1);
  return(
    <section className="kt-sec">
      <div className="container" style={{position:"relative",zIndex:1}}>
        <div className="text-center">
          <span className="kt-tag">✦ Parent Stories ✦</span>
          <h2 className="kt-h">Real Parties,<br/><span>Real Happy Kids</span></h2>
          <p className="kt-sub">Hear from parents — their children's joy, their stories, their most magical moments.</p>
        </div>
        <div className="kt-stats">
          <div className="kt-stat"><div className="kt-sn">{avg}</div><div style={{display:"flex",justifyContent:"center",gap:2,margin:"5px 0 4px"}}>{[1,2,3,4,5].map(n=><span key={n} style={{fontSize:11,color:C.gold}}>★</span>)}</div><div className="kt-sl">Average Rating</div></div>
          <div className="kt-ssep"/><div className="kt-stat"><div className="kt-sn">2000+</div><div className="kt-sl">Happy Parties</div></div>
          <div className="kt-ssep"/><div className="kt-stat"><div className="kt-sn">99%</div><div className="kt-sl">Would Return</div></div>
          <div className="kt-ssep"/><div className="kt-stat"><div className="kt-sn">4.9</div><div className="kt-sl">Google Rating</div></div>
        </div>
        <div style={{marginTop:56}}>
          <div className="row g-4 align-items-stretch">
            <div className="col-lg-7" style={{display:"flex"}}>
              <div className="kt-feat">
                <div className="kt-big-q">"</div>
                <div className="kt-feat-in">
                  <div className="kt-fi-wrap"><img key={ai} src={cur.image} alt={cur.name} className="kt-fi" loading="lazy"/><div className="kt-fi-ovl"/><div className="kt-fi-badge">🎉 {cur.occasion}</div></div>
                  <div style={{marginBottom:14}}><Stars rating={cur.rating} size={15}/></div>
                  <p className="kt-review">"{cur.text}"</p>
                  <div className="kt-div"/>
                  <div className="kt-author">
                    <img src={cur.avatar} alt={cur.name} className="kt-avatar" loading="lazy"/>
                    <div><div className="kt-aname">{cur.name}</div><div className="kt-aloc">{cur.location}</div></div>
                    <div className="kt-v">✓</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-5" style={{display:"flex",flexDirection:"column",gap:16}}>
              {[prev,next].map((rv,i)=>(
                <div key={i} className="kt-prev" onClick={()=>goTo(i===0?(ai-1+REVIEWS.length)%REVIEWS.length:(ai+1)%REVIEWS.length)}>
                  <img src={rv.image} alt={rv.name} className="kt-pi" loading="lazy"/>
                  <div className="kt-pb"><Stars rating={rv.rating} size={11}/><p className="kt-pt">"{rv.text}"</p><div className="kt-pn">{rv.name}</div><div className="kt-pl">{rv.location}</div><span className="kt-pr">Read Full →</span></div>
                </div>
              ))}
            </div>
          </div>
          <div className="kt-nav">
            <button className="kt-arr" onClick={()=>goTo(ai-1)}>‹</button>
            {REVIEWS.map((_,i)=><button key={i} className={`kt-dot${i===ai?" on":""}`} onClick={()=>goTo(i)}/>)}
            <button className="kt-arr" onClick={()=>goTo(ai+1)}>›</button>
          </div>
        </div>
        <div className="kt-plat">
          {[{name:"Google",score:"4.9",count:"200+ reviews"},{name:"Facebook",score:"4.8",count:"150+ reviews"},{name:"JustDial",score:"4.7",count:"80+ reviews"},{name:"Instagram",score:"5.0",count:"50k+ followers"}].map((p,i)=>(
            <React.Fragment key={i}>
              {i>0&&<div className="kt-psep"/>}
              <div className="kt-plat-item"><div className="kt-plat-name">{p.name}</div><div className="kt-plat-row"><span className="kt-plat-sc">{p.score}</span><div style={{display:"flex",gap:2}}>{[1,2,3,4,5].map(n=><span key={n} style={{fontSize:11,color:C.gold}}>★</span>)}</div></div><div className="kt-plat-cnt">{p.count}</div></div>
            </React.Fragment>
          ))}
        </div>
        <div className="kt-cta">
          <h3 className="kt-cta-h">Ready to Create <span>Pure Magic?</span></h3>
          <p className="kt-cta-sub">Join 2000+ happy families who celebrated their child's most special moments with us.</p>
          <div className="kt-cta-row">
            <button onClick={() => window.open("https://wa.me/918341428342?text=Hi%2C%20I%20would%20like%20to%20book%20a%20Kids%20Birthday%20Celebration%20at%20Carnival%20Castle!", "_blank")} className="kt-btn-bk">🎈 Book My Party</button>
            <a href="https://wa.me/918341428342" target="_blank" rel="noopener noreferrer" className="kt-btn-wa"><i className="bi bi-whatsapp"/> Chat With Us</a>
          </div>
        </div>
      </div>
    </section>
  );
});

const ScrollRevealWatcher=memo(()=>{
  useEffect(()=>{
    const o=new IntersectionObserver(e=>e.forEach(x=>{if(x.isIntersecting)x.target.classList.add("in");}),{threshold:.08});
    const run=()=>document.querySelectorAll(".rv:not(.in)").forEach(el=>o.observe(el));
    run();const t=setTimeout(run,600);
    return()=>{o.disconnect();clearTimeout(t);};
  },[]);
  return null;
});

function GalleryEntranceObserver(){
  useEffect(()=>{
    const items=document.querySelectorAll(".gal-bento-item");
    const o=new IntersectionObserver(entries=>{entries.forEach(e=>{if(e.isIntersecting){const i=Array.from(items).indexOf(e.target);e.target.style.transitionDelay=`${i*.08}s`;e.target.classList.add("in");o.unobserve(e.target);}});},{threshold:.1});
    items.forEach(el=>o.observe(el));
    return()=>o.disconnect();
  },[]);
  return null;
}

/* ─── Main Page ──────────────────────────────────────────────────── */
export default function KidsPage(){
  const [loading,setLoading]=useState(true);
  const [showPopup,setShowPopup]=useState(false);
  const [popupImg,setPopupImg]=useState("");
  const [galIdx,setGalIdx]=useState(0);
  const galRef=useRef(null);
  const navigate=useNavigate();

  useEffect(()=>{setTimeout(()=>setLoading(false),400);},[]);
  const openBooking = () => {
    window.open("https://wa.me/918341428342?text=Hi%2C%20I%20would%20like%20to%20book%20a%20Kids%20Birthday%20Celebration%20at%20Carnival%20Castle!", "_blank");
  };
  const requestCallback = () => {
    window.open("tel:8341428342", "_self");
  };
  const galItem=useCallback(src=><img src={src} alt="" style={{width:"100%",display:"block",borderRadius:14}}/>,[]);
  const galThumb=useCallback(src=><img src={src} alt="" style={{width:58,height:58,objectFit:"cover",display:"block",borderRadius:8}}/>,[]);

  if(loading)return(
    <div style={{background:C.dark,height:"100vh",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:18}}>
      <div style={{width:68,height:68,border:`4px solid ${C.primaryPale}`,borderTop:`4px solid ${C.gold}`,borderRadius:"50%",animation:"_spin 1s linear infinite"}}/>
      <p style={{color:C.gold,fontWeight:600,letterSpacing:2,fontSize:14}}>Loading Magic…</p>
      <style>{`@keyframes _spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return(
    <>
      <style>{GLOBAL_CSS}</style>
      <Helmet>
        <title>Carnival Castle Kids | Magical Birthday Parties in Hyderabad</title>
        <meta name="description" content="Themed birthday parties for kids — Princess, Superhero, Dino & 20+ themes. Premium packages from ₹4,999."/>
      </Helmet>
      <ScrollRevealWatcher/>
      <div className="kp-root">
        <Header/>
        <OccasionsNav currentPath="/kids"/>

        {/* Ticker */}
        <div className="kp-ticker"><div className="kp-ticker-track">
          {[...Array(4)].map((_,k)=>["🎈 20+ Magical Themes","⭐ 2000+ Happy Parties","🛡️ 100% Child Safe","📸 Photography Included","⚡ Book in 2 Minutes","🎂 Custom Cakes Available"].map((item,i)=><span key={`${k}-${i}`} className="kp-ticker-item">{item}</span>))}
        </div></div>

        {/* Hero */}
        <section className="kp-hero">
          <div className="kp-hero-orb" style={{width:500,height:500,background:`radial-gradient(circle,${C.primary}36 0%,transparent 70%)`,top:-100,right:-80}}/>
          <div className="kp-hero-orb" style={{width:300,height:300,background:"radial-gradient(circle,rgba(236,72,153,.16) 0%,transparent 70%)",bottom:80,left:-60}}/>
          <div className="container kp-hero-cnt">
            <div className="row align-items-center g-5">
              <div className="col-lg-6" style={{animation:"_fadeUp .9s cubic-bezier(.16,1,.3,1) both"}}>
                <div className="kp-hero-badge">🎉 Hyderabad's #1 Kids Party Venue</div>
                <h1 className="kp-hero-title pf">Create <span>Magical</span><br/>Birthday Memories</h1>
                <p className="kp-hero-sub">From princess castles to dino adventures — 20+ incredible themes, professional setups, and memories your child will cherish forever.</p>
                <div style={{display:"flex",gap:14,flexWrap:"wrap",alignItems:"center"}}>
                  <button onClick={openBooking} className="cc-btn-gold">🎈 Book a Party</button>
                  <button onClick={requestCallback} className="cc-btn-outline" style={{borderColor:"rgba(201,168,76,.5)",color:"#E8D5A0"}}>📞 Request Callback</button>
                </div>
                <div className="kp-trust-row">
                  {[["2000+","Parties"],["4.9★","Rating"],["20+","Themes"]].map(([n,l],idx)=>(
                    <React.Fragment key={l}>
                      {idx > 0 && <div className="kp-trust-sep"/>}
                      <div className="kp-trust-item"><div className="num">{n}</div><div className="lbl">{l}</div></div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
              <div className="col-lg-6" style={{animation:"_fadeUp .9s cubic-bezier(.16,1,.3,1) .2s both"}}>
                <HeroVideo/>
              </div>
            </div>
          </div>
        </section>

        <ReelsSection/>
        <VideoTestimonials/>
        <ThemeCarousel navigate={navigate}/>

        {/* Services */}
        <section className="kp-sec" style={{background:"#fff",overflow:"hidden"}}>
          <div className="container">
            <div className="text-center rv" style={{marginBottom:44}}>
              <span className="cc-tag">✦ Party Services ✦</span>
              <h2 className="cc-h">Everything You <span>Need</span> for the Perfect Party</h2>
              <p className="cc-sub">Decor, cakes, food, photography — curated to make your child's day perfect.</p>
            </div>
            <div className="kp-divider"><div className="kp-dl"/><div className="kp-dg"/><div className="kp-dl"/></div>
            <div className="svc-grid rv">
              {SERVICE_CARDS.map((s,i)=>(
                <div key={i} className="svc-card">
                  <div className="svc-img-wrap">
                    <img
                      src={s.img}
                      alt={s.label}
                      className="svc-img"
                      loading="lazy"
                      onError={(e) => {
                        if (s.fallback && e.currentTarget.src !== s.fallback) {
                          e.currentTarget.src = s.fallback;
                        }
                      }}
                    />
                    <div className="svc-glow"/>
                    <div className="svc-num">0{i+1}</div>
                    <div className="svc-pill">{["✨ Trending","🎂 Custom","🍽️ Fresh","📸 Star Moments"][i]}</div>
                  </div>
                  <div className="svc-body">
                    <div className="svc-float-icon">{["🎪","🎂","🍔","📸"][i]}</div>
                    <div className="svc-title pf">{s.label}</div>
                    <span className="svc-line"/>
                    <p className="svc-desc">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Activities */}
        <section className="kp-sec" style={{background:C.cream}}>
          <div className="container">
            <div className="text-center rv" style={{marginBottom:44}}>
              <span className="cc-tag">✦ Fun Activities ✦</span>
              <h2 className="cc-h">Keep the Kids <span>Entertained All Day</span></h2>
            </div>
            <div className="row g-4">
              {KID_ACTIVITIES.map((act,i)=>(
                <div key={i} className={`col-md-3 col-6 rv d${i+1}`}>
                  <div className="act-card">
                    <div style={{overflow:"hidden",height:185}}><img src={act.img} alt={act.name} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}} loading="lazy"/></div>
                    <div style={{padding:"14px"}}><h3 style={{fontSize:15,fontWeight:700,marginBottom:5,color:C.dark}}>{act.name}</h3><div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#888"}}><span>⏱️ {act.dur}</span><span>👶 {act.age}</span></div></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* <WrittenReviews/> */}
        <TestimonialsSection />


        {/* Gallery */}
        <section className="kp-sec" style={{background:C.cream,overflow:"hidden"}}>
          <div className="container">
            <div className="text-center rv" style={{marginBottom:50}}>
              <span className="cc-tag">✦ Happy Moments ✦</span>
              <h2 className="cc-h">Real Memories from <span>Real Parties</span></h2>
              <p className="cc-sub">Captured moments from our little guests' most magical days.</p>
            </div>
            <div className="gal-bento rv">
              {KID_GALLERY.map((img,i)=>(
                <div key={i} className={`gal-bento-item gbi-${i+1}`} onClick={()=>{setGalIdx(i);galRef.current?.show();}}>
                  <img src={img} alt={`Party ${i+1}`} loading="lazy"/><div className="gal-bento-ovl">🔍</div>
                </div>
              ))}
            </div>
            <Galleria ref={galRef} value={KID_GALLERY} numVisible={5} activeIndex={galIdx} onItemChange={e=>setGalIdx(e.index)} circular fullScreen showItemNavigators showThumbnails={false} item={galItem} thumbnail={galThumb}/>
            <div style={{display:"flex",gap:14,justifyContent:"center",marginTop:34,flexWrap:"wrap"}}>
              <button onClick={openBooking} className="cc-btn">Book Your Party →</button>
              <a href="https://wa.me/918341428342" target="_blank" rel="noopener noreferrer" className="wa-btn"><i className="bi bi-whatsapp"/> Chat With Us</a>
            </div>
          </div>
          <GalleryEntranceObserver/>
        </section>

        {/* CTA */}
        <section className="kp-cta-sec kp-sec-sm">
          <div className="container text-center" style={{position:"relative",zIndex:1}}>
            <span style={{fontSize:50}}>🎂</span>
            <h2 className="pf" style={{fontSize:"clamp(24px,4vw,42px)",color:"#fff",fontWeight:900,marginTop:12,marginBottom:12}}>
              Ready to Create <span style={{background:`linear-gradient(135deg,${C.goldLight},${C.goldBright})`,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>Pure Magic?</span>
            </h2>
            <p style={{color:"rgba(255,255,255,.63)",fontSize:15,marginBottom:26,maxWidth:460,margin:"0 auto 26px"}}>Book your child's dream party today. We handle everything so you just celebrate.</p>
            <button className="cc-btn-gold" style={{fontSize:15,padding:"15px 40px"}} onClick={openBooking}>🎈 Book Your Party Now</button>
            <p style={{color:"rgba(255,255,255,.32)",fontSize:12,marginTop:12}}>Join 2000+ happy families who celebrated with us</p>
          </div>
        </section>

        <Footer/>
        <ToastContainer position="top-center" autoClose={4000} theme="dark"/>
        <PopupModal show={showPopup} handleClose={()=>setShowPopup(false)} image={popupImg}/>
      </div>
    </>
  );
}